import React, { useState, useEffect } from 'react';
import './GamePlanning.css';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';
import { dowLong, dowShort } from '../utils/dayOfWeek';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function GamePlanning() {
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('game-planner', 'game-planner');
  // Each day-level bulk button needs its own explicit grant on top of
  // page edit — a profile with plain Game Planner edit sees none of them.
  const canAiAllocate = !readOnly && canEdit('game-planner', 'ai-allocate');
  const canImportRoster = !readOnly && canEdit('game-planner', 'import-roster');
  const canAllAttended = !readOnly && canEdit('game-planner', 'all-attended');
  const canClearRoster = !readOnly && canEdit('game-planner', 'clear-roster');
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [games, setGames] = useState([]);

  // Date range: 3 weeks behind and 3 weeks ahead
  const today = new Date();
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 21);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 21);
  const minDateStr = `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}-${String(minDate.getDate()).padStart(2, '0')}`;
  const maxDateStr = `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}-${String(maxDate.getDate()).padStart(2, '0')}`;
  const [refereeOptions, setRefereeOptions] = useState([]);
  const [benchOfficialOptions, setBenchOfficialOptions] = useState([]);
  const [gameTimeOffset, setGameTimeOffset] = useState(11);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availabilityCache, setAvailabilityCache] = useState({}); // Cache availability info
  const [dailyCostData, setDailyCostData] = useState(null); // Budget and cost data for the day
  const [divisionMap, setDivisionMap] = useState({}); // Map division names to age groups
  const [qualificationDataMap, setQualificationDataMap] = useState({}); // Map qualificationId to full qualification data
  const [isDayLocked, setIsDayLocked] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [personTeamAssignments, setPersonTeamAssignments] = useState([]); // All person-team assignments
  const [personTeamExclusions, setPersonTeamExclusions] = useState([]); // All person-team exclusions (not rostered on that team's games)

  // Auto-allocation state
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [allocateStartDate, setAllocateStartDate] = useState('');
  const [allocateEndDate, setAllocateEndDate] = useState('');
  // Optional operator-chosen comparison ("Last Period") window. Defaults
  // to the week before the proposed range (start-7 … end-7); the
  // operator can override it to compare against any prior period. The
  // per-day breakdown pairs proposed day i with comparison day i.
  const [allocateCompStartDate, setAllocateCompStartDate] = useState('');
  const [allocateCompEndDate, setAllocateCompEndDate] = useState('');
  const [allocateLoading, setAllocateLoading] = useState(false);
  const [allocateResults, setAllocateResults] = useState(null);
  const [applyingAllocation, setApplyingAllocation] = useState(false);
  // Which tab is shown in the AI Allocate result modal: 'person' (the
  // existing per-person breakdown table) or 'game' (the new per-game
  // preview). Resets when results clear.
  const [allocateView, setAllocateView] = useState('person');

  // Roster-import state. Upload an image / PDF → backend OCRs via Claude
  // vision → returns a preview the admin reviews before applying.
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importDate, setImportDate] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importError, setImportError] = useState('');
  const [applyingImport, setApplyingImport] = useState(false);

  // "All Attended" — bulk-mark every referee + bench slot on the
  // selected day as attended.
  const [allAttendedLoading, setAllAttendedLoading] = useState(false);
  const [clearRosterLoading, setClearRosterLoading] = useState(false);

  // Game reports (court supervisor) — one report per game: forfeit,
  // tech fouls, injuries, general notes. gameReports maps gameId → the
  // stored report so each card's Report button knows grey vs green and
  // can show a hover summary without extra requests.
  const [gameReports, setGameReports] = useState({});
  const [reportModalGame, setReportModalGame] = useState(null);
  const [reportForfeit, setReportForfeit] = useState('');
  const [reportTechFouls, setReportTechFouls] = useState([]);
  const [reportInjuries, setReportInjuries] = useState([]);
  const [reportNotes, setReportNotes] = useState('');
  const [reportSaving, setReportSaving] = useState(false);
  const [reportError, setReportError] = useState('');
  // Both teams' players (from Basketball Connect) for the report's name drop-downs.
  const [reportRoster, setReportRoster] = useState(null);
  // Suspended players by gameId for the day (red Suspension button).
  const [gameSuspensions, setGameSuspensions] = useState({});
  const [suspensionModalGame, setSuspensionModalGame] = useState(null);

  // Daily Notes — one day-level General Info comment (not tied to a
  // match). Lands in the Court Supervisors report's General Info
  // section without the per-game columns.
  const [dayNotes, setDayNotes] = useState('');
  const [showDayNotesModal, setShowDayNotesModal] = useState(false);
  const [dayNotesDraft, setDayNotesDraft] = useState('');
  const [dayNotesSaving, setDayNotesSaving] = useState(false);
  const [dayNotesError, setDayNotesError] = useState('');


  const ALL_COURTS = ['Court 1', 'Court 2', 'Court 3', 'Court 4', 'Beaton'];

  // Hide any court that has no games on the currently-selected day so
  // the grid only shows columns the operator can actually do something
  // with (operator request 16 May 2026: "Beaton on the 4th May" was
  // empty but still rendered, wasting screen real estate).
  // Falls back to ALL_COURTS when games haven't loaded yet so the
  // header doesn't briefly collapse to nothing on first paint.
  const COURTS = (games.length === 0)
    ? ALL_COURTS
    : ALL_COURTS.filter(c => games.some(g => String(g.court).trim() === c));

  // Build the grid-template-columns string from the actual number of
  // visible courts so empty courts don't reserve a phantom column. The
  // CSS file has a 5-court default for first paint; this overrides it
  // inline once we know how many courts are actually in play.
  const gridTemplateColumnsStyle = `80px ${COURTS.map(() => 'minmax(0, 1fr)').join(' ')}`;

  useEffect(() => {
    fetchReferees();
    fetchBenchOfficials();
    fetchGameTimeOffset();
    fetchDivisionMapping();
    fetchPersonTeamAssignments();
    fetchPersonTeamExclusions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPersonTeamAssignments = async () => {
    try {
      const response = await fetch(`${API_BASE}/person-team-assignments`);
      if (response.ok) {
        setPersonTeamAssignments(await response.json());
      }
    } catch (err) {
      console.error('Error fetching person team assignments:', err);
    }
  };

  const fetchPersonTeamExclusions = async () => {
    try {
      const response = await fetch(`${API_BASE}/person-team-exclusions`);
      if (response.ok) {
        setPersonTeamExclusions(await response.json());
      }
    } catch (err) {
      console.error('Error fetching person team exclusions:', err);
    }
  };

  const fetchGameTimeOffset = async () => {
    try {
      const response = await fetch(`${API_BASE}/settings/game-time-offset`);
      if (response.ok) {
        const data = await response.json();
        setGameTimeOffset(data.offset);
      }
    } catch (err) {
      console.error('Error fetching game time offset:', err);
    }
  };

  const fetchDivisionMapping = async () => {
    try {
      const response = await fetch(`${API_BASE}/division-mappings`);
      console.log('Division mapping response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Division mapping response data:', data, 'Type:', typeof data);

        // Handle both direct array and { mappings: [...] } formats
        let mappings = [];
        if (Array.isArray(data)) {
          mappings = data;
          console.log('Response is an array');
        } else if (data.mappings && Array.isArray(data.mappings)) {
          mappings = data.mappings;
          console.log('Response has mappings property');
        } else {
          console.error('Unexpected response format. Expected array or { mappings: [...] }');
        }

        const map = {};
        mappings.forEach(m => {
          map[m.divisionName] = m.ageGroup;
        });
        setDivisionMap(map);
        console.log('Division map loaded with', Object.keys(map).length, 'entries:', map);
      } else {
        console.error('Division mapping fetch failed:', response.status);
      }
    } catch (err) {
      console.error('Error fetching division mapping:', err);
    }
  };

  useEffect(() => {
    fetchGamesByDate();
    fetchDailyCost();
    fetchLockStatus();
    fetchGameReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchGameReports = async () => {
    try {
      const res = await fetch(`${API_BASE}/games/reports/by-day/${selectedDate}`);
      setGameReports(res.ok ? await res.json() : {});
    } catch {
      setGameReports({});
    }
    try {
      const res = await fetch(`${API_BASE}/games/suspensions/by-day/${selectedDate}`);
      setGameSuspensions(res.ok ? await res.json() : {});
    } catch {
      setGameSuspensions({});
    }
    try {
      const res = await fetch(`${API_BASE}/games/day-notes/${selectedDate}`);
      const data = res.ok ? await res.json() : {};
      setDayNotes(data.generalNotes || '');
    } catch {
      setDayNotes('');
    }
  };

  const saveDayNotes = async () => {
    setDayNotesSaving(true);
    setDayNotesError('');
    try {
      const res = await auditFetch(`${API_BASE}/games/day-notes/${selectedDate}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ generalNotes: dayNotesDraft }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || `HTTP ${res.status}`);
      setDayNotes(data.generalNotes || '');
      setShowDayNotesModal(false);
    } catch (e) {
      setDayNotesError(e.message || 'Failed to save daily notes');
    } finally {
      setDayNotesSaving(false);
    }
  };

  const reportHasContent = (r) => !!r && !!(
    r.forfeitTeam ||
    (r.techFouls || []).length > 0 ||
    (r.injuries || []).length > 0 ||
    (r.generalNotes || '').trim()
  );

  // Plain-text summary of a stored report for the button's hover tooltip.
  const reportSummary = (r) => {
    if (!reportHasContent(r)) return 'No report entered yet — click to add one.';
    const lines = [];
    if (r.forfeitTeam) lines.push(`Forfeit: ${r.forfeitTeam}`);
    (r.techFouls || []).forEach(t => {
      lines.push(`Tech foul: ${t.name}${t.team ? ` (${t.team})` : ''} — ${t.count} foul${t.count === 2 ? 's' : ''}, ${t.severity || 'Low'}${t.ejection ? ', EJECTED' : ''}${t.comments ? ` (${t.comments})` : ''}`);
    });
    (r.injuries || []).forEach(i => {
      lines.push(`Injury: ${i.name} — ${i.injury}${i.treatment ? ` → ${i.treatment}` : ''}${i.comments ? ` (${i.comments})` : ''}`);
    });
    if ((r.generalNotes || '').trim()) lines.push(`Notes: ${r.generalNotes.trim()}`);
    return lines.join('\n');
  };

  const openReportModal = (game) => {
    const r = gameReports[game.id] || {};
    setReportForfeit(r.forfeitTeam || '');
    setReportTechFouls((r.techFouls || []).map(t => ({ ...t })));
    setReportInjuries((r.injuries || []).map(i => ({ ...i })));
    setReportNotes(r.generalNotes || '');
    setReportError('');
    setReportRoster(null);
    setReportModalGame(game);
    fetch(`${API_BASE}/games/${game.id}/players`)
      .then(res => (res.ok ? res.json() : { teams: [] }))
      .then(data => setReportRoster(data.teams || []))
      .catch(() => setReportRoster([]));
  };

  // Player picker for tech fouls / injuries: the two teams' players grouped
  // by team, or "Someone else" to type a name.
  const playerValue = (row) => (row.participantId ? `${row.team}|||${row.participantId}` : (row.name || row._other ? '__other' : ''));
  const applyPlayerChoice = (row, value) => {
    if (value === '__other') return { ...row, participantId: null, team: row.team || '', _other: true };
    if (!value) return { ...row, participantId: null, team: '', name: '', _other: false };
    const [team, pid] = value.split('|||');
    const pl = (reportRoster || []).find(tm => tm.name === team)?.players.find(x => String(x.participantId) === pid);
    return { ...row, participantId: Number(pid), team, name: pl ? pl.name : row.name, _other: false };
  };
  const renderPlayerPicker = (row, onChange) => (
    <>
      <select
        value={playerValue(row)}
        disabled={readOnly}
        onChange={e => onChange(applyPlayerChoice(row, e.target.value))}
        style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4, width: 200 }}
      >
        <option value="">{reportRoster === null ? 'Loading players…' : 'Choose player…'}</option>
        {(reportRoster || []).map(tm => (
          <optgroup key={tm.name} label={tm.players.length ? tm.name : `${tm.name} (no players found)`}>
            {tm.players.map(pl => <option key={pl.participantId} value={`${tm.name}|||${pl.participantId}`}>{pl.name}</option>)}
          </optgroup>
        ))}
        <option value="__other">Someone else (type name)</option>
      </select>
      {playerValue(row) === '__other' && (
        <>
          <input
            type="text" placeholder="Person's name" value={row.name} disabled={readOnly}
            onChange={e => onChange({ ...row, name: e.target.value })}
            style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4, width: 150 }}
          />
          <select
            value={row.team || ''} disabled={readOnly}
            onChange={e => onChange({ ...row, team: e.target.value })}
            style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4 }}
          >
            <option value="">Team…</option>
            <option value={reportModalGame.team1}>{reportModalGame.team1}</option>
            <option value={reportModalGame.team2}>{reportModalGame.team2}</option>
          </select>
        </>
      )}
    </>
  );

  const saveGameReport = async () => {
    if (!reportModalGame) return;
    setReportSaving(true);
    setReportError('');
    try {
      const res = await auditFetch(`${API_BASE}/games/${reportModalGame.id}/report`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          forfeitTeam: reportForfeit,
          techFouls: reportTechFouls,
          injuries: reportInjuries,
          generalNotes: reportNotes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || `HTTP ${res.status}`);
      setGameReports(prev => {
        const next = { ...prev };
        if (data.report) next[reportModalGame.id] = data.report;
        else delete next[reportModalGame.id];
        return next;
      });
      setReportModalGame(null);
    } catch (e) {
      setReportError(e.message || 'Failed to save report');
    } finally {
      setReportSaving(false);
    }
  };

  const fetchLockStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/locked-days/${selectedDate}?source=game`);
      if (response.ok) {
        const data = await response.json();
        setIsDayLocked(data.isLocked);
      }
    } catch (err) {
      console.error('Error fetching lock status:', err);
    }
  };

  const handleLockDay = async () => {
    try {
      setLockLoading(true);
      const response = await auditFetch(`${API_BASE}/locked-days`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, source: 'game' })
      });

      if (response.ok) {
        setIsDayLocked(true);
        setSuccess('Day locked successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to lock day');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLockLoading(false);
    }
  };

  const handleOpenAllocate = () => {
    // Default to current week (Monday to Sunday)
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setAllocateStartDate(fmt(monday));
    setAllocateEndDate(fmt(sunday));
    // Default comparison window = the week immediately before.
    const compMon = new Date(monday); compMon.setDate(monday.getDate() - 7);
    const compSun = new Date(sunday); compSun.setDate(sunday.getDate() - 7);
    setAllocateCompStartDate(fmt(compMon));
    setAllocateCompEndDate(fmt(compSun));
    setAllocateResults(null); setAllocateView('person');
    setShowAllocateModal(true);
  };

  const handleRunAllocation = async () => {
    setAllocateLoading(true);
    setError('');
    try {
      const response = await auditFetch(`${API_BASE}/games/auto-allocate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate: allocateStartDate,
          endDate: allocateEndDate,
          comparisonStartDate: allocateCompStartDate || undefined,
          comparisonEndDate: allocateCompEndDate || undefined,
        })
      });
      if (response.ok) {
        const data = await response.json();
        setAllocateResults(data);
      } else {
        const err = await response.json();
        setError(err.error || 'Auto-allocation failed');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setAllocateLoading(false);
    }
  };

  const handleApplyAllocation = async () => {
    if (!allocateResults) return;
    if (!window.confirm(`Apply this allocation to ${allocateResults.totalGames} games? This will overwrite any existing assignments (except locked days and confirmed attendance).`)) return;
    setApplyingAllocation(true);
    try {
      const response = await auditFetch(`${API_BASE}/games/auto-allocate/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allocations: allocateResults.allocations })
      });
      if (response.ok) {
        setSuccess('Allocation applied successfully!');
        setShowAllocateModal(false);
        setAllocateResults(null); setAllocateView('person');
        fetchGamesByDate();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to apply allocation');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setApplyingAllocation(false);
    }
  };

  // ==================== AI ALLOCATE EXPORTS ====================
  // CSV via blob URL, PDF via the browser's print dialog (same pattern
  // as Reports.jsx). Both pull straight from allocateResults — no extra
  // API call needed; the modal already has all the data it needs.

  const downloadFile = (filename, content, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // CSV cell escape: quote everything, double internal quotes.
  const csvCell = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const csvLine = (cells) => cells.map(csvCell).join(',');

  // Build a per-game row array. Sorted by scheduledTime so the file
  // matches what's on screen.
  const buildPerGameRows = () => {
    if (!allocateResults) return [];
    const rows = [['Day', 'Date', 'Time', 'Court', 'Division', 'Matchup',
      'Crew Chief', 'Referee 2', 'Referee 3', 'Referee 4', 'Bench', 'Status']];
    [...allocateResults.allocations]
      .sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime))
      .forEach(a => {
        const t = new Date(a.scheduledTime);
        const date = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        const time = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
        const refs = a.referees || [];
        const bench = a.benchOfficials || [];
        const slot = (s) => s ? (s.name || `#${s.personId}`) : '';
        const benchAll = bench.map(slot).filter(Boolean).join(', ');
        rows.push([dowShort(date), date, time, a.court, a.division || '', `${a.team1} vs ${a.team2}`,
          slot(refs[0]), slot(refs[1]), slot(refs[2]), slot(refs[3]), benchAll,
          a.skipped ? `Skipped: ${a.reason}` : 'Proposed']);
      });
    return rows;
  };

  const buildPerPersonRows = () => {
    if (!allocateResults) return [];
    const rows = [['Name', 'Referee Games', 'Bench Games', 'Total Games', 'Pay']];
    allocateResults.summary.forEach(p => {
      rows.push([p.name, p.refereeGames || 0, p.benchGames || 0, p.totalGames, `$${p.totalPay.toFixed(2)}`]);
    });
    return rows;
  };

  const exportPerGameCsv = () => {
    const rows = buildPerGameRows();
    if (rows.length <= 1) return;
    const csv = rows.map(csvLine).join('\n');
    downloadFile(`ai-allocate-per-game-${allocateStartDate}_to_${allocateEndDate}.csv`, csv, 'text/csv;charset=utf-8;');
  };

  const exportPerPersonCsv = () => {
    const rows = buildPerPersonRows();
    if (rows.length <= 1) return;
    const csv = rows.map(csvLine).join('\n');
    downloadFile(`ai-allocate-per-person-${allocateStartDate}_to_${allocateEndDate}.csv`, csv, 'text/csv;charset=utf-8;');
  };

  // PDF via print dialog. Opens a new window with formatted HTML and
  // triggers the browser's print dialog — user picks "Save as PDF".
  // Same pattern as Reports.jsx so the styling matches the rest of
  // the app's printed output.
  const openPrintWindow = (title, subtitle, bodyHtml) => {
    const w = window.open('', '_blank');
    if (!w) { setError('Please allow pop-ups to print / save as PDF.'); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
      body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
      h1 { color: #A4161A; font-size: 22px; margin-bottom: 5px; }
      .subtitle { color: #666; font-size: 14px; margin-bottom: 20px; }
      table { width: 100%; border-collapse: collapse; margin-top: 10px; }
      th { background: #A4161A; color: white; text-align: left; padding: 8px; font-size: 12px; }
      td { padding: 6px 8px; border-bottom: 1px solid #e0e0e0; font-size: 12px; vertical-align: top; }
      tr:nth-child(even) { background: #f9f9f9; }
      .skipped { color: #e65100; font-style: italic; }
      .summary-cards { display: flex; gap: 12px; margin: 12px 0 20px; }
      .summary-card { flex: 1; border: 1px solid #ddd; border-radius: 8px; padding: 10px; text-align: center; }
      .summary-card .label { font-size: 10px; text-transform: uppercase; color: #666; }
      .summary-card .value { font-size: 18px; font-weight: 700; color: #1565c0; margin-top: 4px; }
      @media print { body { padding: 0; } }
    </style></head><body>
      <h1>SwishIQ — AI Allocate ${title}</h1>
      <div class="subtitle">${subtitle}</div>
      ${bodyHtml}
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 500);
  };

  const exportPerGamePdf = () => {
    if (!allocateResults) return;
    const rows = buildPerGameRows();
    const cs = allocateResults.costSummary;
    const summaryHtml = cs ? `
      <div class="summary-cards">
        <div class="summary-card"><div class="label">Last Period</div><div class="value">$${cs.lastPeriod.actualCost.toFixed(2)}</div><div class="label">${cs.lastPeriod.gameCount} games · ${cs.lastPeriod.slotCount} slots</div></div>
        <div class="summary-card"><div class="label">Budget</div><div class="value">$${cs.budgetForRange.toFixed(2)}</div><div class="label">${cs.rangeDays} day${cs.rangeDays === 1 ? '' : 's'}</div></div>
        <div class="summary-card"><div class="label">Proposed</div><div class="value">$${cs.proposedCost.toFixed(2)}</div><div class="label">${cs.proposedGameCount} games · ${cs.proposedSlotCount} slots</div></div>
      </div>` : '';
    const headerCells = rows[0].map(h => `<th>${h}</th>`).join('');
    const bodyRows = rows.slice(1).map(r => {
      const status = r[r.length - 1];
      const isSkipped = String(status).startsWith('Skipped');
      return `<tr${isSkipped ? ' class="skipped"' : ''}>${r.map(c => `<td>${String(c == null ? '' : c)}</td>`).join('')}</tr>`;
    }).join('');
    openPrintWindow('— Per Game', `${allocateStartDate} → ${allocateEndDate}`,
      summaryHtml + `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`);
  };

  const exportPerPersonPdf = () => {
    if (!allocateResults) return;
    const rows = buildPerPersonRows();
    const cs = allocateResults.costSummary;
    const summaryHtml = cs ? `
      <div class="summary-cards">
        <div class="summary-card"><div class="label">Last Period</div><div class="value">$${cs.lastPeriod.actualCost.toFixed(2)}</div><div class="label">${cs.lastPeriod.gameCount} games · ${cs.lastPeriod.slotCount} slots</div></div>
        <div class="summary-card"><div class="label">Budget</div><div class="value">$${cs.budgetForRange.toFixed(2)}</div><div class="label">${cs.rangeDays} day${cs.rangeDays === 1 ? '' : 's'}</div></div>
        <div class="summary-card"><div class="label">Proposed</div><div class="value">$${cs.proposedCost.toFixed(2)}</div><div class="label">${cs.proposedGameCount} games · ${cs.proposedSlotCount} slots</div></div>
      </div>` : '';
    const headerCells = rows[0].map(h => `<th>${h}</th>`).join('');
    const bodyRows = rows.slice(1).map(r => `<tr>${r.map(c => `<td>${String(c == null ? '' : c)}</td>`).join('')}</tr>`).join('');
    openPrintWindow('— Per Person', `${allocateStartDate} → ${allocateEndDate}`,
      summaryHtml + `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`);
  };

  // ==================== ALL ATTENDED ====================
  // Bulk-mark every referee + bench slot on the currently-selected day
  // as attended. Single confirm dialog, then one POST. Refreshes the
  // games list afterwards so the chips re-render with the dark colour.
  const handleAllAttended = async () => {
    if (!selectedDate) { setError('Pick a date first'); return; }
    if (!window.confirm(`Mark every referee + bench official assignment on ${selectedDate} as attended? Only flips slots that aren't already attended; nothing is reset to not-attended.`)) return;
    setAllAttendedLoading(true);
    setError('');
    try {
      const response = await auditFetch(`${API_BASE}/games/by-day/${selectedDate}/mark-all-attended`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || `Couldn't mark all attended (HTTP ${response.status})`);
      } else {
        setSuccess(data.message || 'All attended.');
        await fetchGamesByDate();
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (e) {
      setError('Error: ' + e.message);
    } finally {
      setAllAttendedLoading(false);
    }
  };

  // ==================== CLEAR ROSTER ====================
  // Destructive: deletes every referee + bench official assignment slot
  // for every non-deleted game on the selected day. Removes both the
  // allocations AND the attendance ticks (since attendance lives on the
  // slot row). Locked days are protected backend-side. Two confirms so
  // an accidental click on the red button can't wipe the day.
  const handleClearRoster = async () => {
    if (!selectedDate) { setError('Pick a date first'); return; }
    if (isDayLocked) { setError('Day is locked — unlock it first via Payroll → Unlock Day.'); return; }
    if (!window.confirm(`CLEAR THE ROSTER for ${selectedDate}?\n\nThis will permanently delete EVERY referee and bench official assignment for the day, including any that have already been marked attended.\n\nThis cannot be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? Click Cancel to abort.`)) return;
    setClearRosterLoading(true);
    setError('');
    try {
      const response = await auditFetch(`${API_BASE}/games/by-day/${selectedDate}/clear-roster`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || `Couldn't clear roster (HTTP ${response.status})`);
      } else {
        setSuccess(data.message || 'Roster cleared.');
        await fetchGamesByDate();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (e) {
      setError('Error: ' + e.message);
    } finally {
      setClearRosterLoading(false);
    }
  };

  // ==================== ROSTER IMPORT ====================
  // Upload an image / PDF of an externally-prepared roster, OCR via the
  // backend (Claude vision), preview the resolved name → person matches
  // and game lookups, then apply.

  const handleImportPreview = async () => {
    setImportError('');
    if (!importFile) { setImportError('Pick a file first'); return; }
    if (!importDate) { setImportError('Pick the roster date'); return; }
    setImportLoading(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('rosterDate', importDate);
      const res = await auditFetch(`${API_BASE}/games/import-roster/preview`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setImportError(data.error || `Preview failed (HTTP ${res.status})`);
      } else {
        setImportPreview(data);
      }
    } catch (e) {
      setImportError('Error: ' + e.message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportApply = async () => {
    if (!importPreview || !Array.isArray(importPreview.allocations)) return;
    if (!window.confirm(`Apply ${importPreview.allocations.length} game(s) from the imported roster? Existing assignments on those games will be replaced (locked days + games with confirmed attendance are skipped).`)) return;
    setApplyingImport(true);
    setImportError('');
    try {
      const res = await auditFetch(`${API_BASE}/games/import-roster/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allocations: importPreview.allocations })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setImportError(data.error || `Apply failed (HTTP ${res.status})`);
      } else {
        const skipped = (data.skipped || []).length;
        setSuccess(`Imported roster: ${data.updated} game(s) updated${skipped ? `, ${skipped} skipped` : ''}.`);
        setShowImportModal(false);
        setImportPreview(null);
        setImportFile(null);
        fetchGamesByDate();
        setTimeout(() => setSuccess(''), 6000);
      }
    } catch (e) {
      setImportError('Error: ' + e.message);
    } finally {
      setApplyingImport(false);
    }
  };

  // Check if a game's scheduled time has started (attendance available from game start)
  const isGameTimePassed = (scheduledTime) => {
    if (!scheduledTime) return false;
    const gameTime = new Date(scheduledTime);
    return new Date() >= gameTime;
  };

  const handleAttendanceToggle = async (gameId, personId, type, currentStatus) => {
    if (isDayLocked) return;
    try {
      const newStatus = !currentStatus;
      const response = await auditFetch(`${API_BASE}/games/${gameId}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personId, type, attended: newStatus })
      });

      if (response.ok) {
        setSuccess('Attendance updated!');
        const arrayKey = type === 'referee' ? 'referees' : 'benchOfficials';
        setGames(games.map(game => {
          if (game.id !== gameId) return game;
          return {
            ...game,
            [arrayKey]: (game[arrayKey] || []).map(slot =>
              slot.personId === personId
                ? { ...slot, attended: newStatus, attendedAt: newStatus ? new Date().toISOString() : null }
                : slot
            )
          };
        }));
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update attendance');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // Wrapper for assignment changes that prompts confirmation after game time has passed
  const handleAssignWithConfirm = (game, ...assignArgs) => {
    if (isGameTimePassed(game.scheduledTime)) {
      if (!window.confirm('This game has already been played. Are you sure you want to change the assignment?')) {
        // Re-fetch to reset the dropdown to its previous value
        fetchGamesByDate();
        return;
      }
    }
    handleAssign(...assignArgs);
  };


  const fetchDailyCost = async () => {
    try {
      const response = await fetch(`${API_BASE}/games/daily-cost/${selectedDate}`);
      if (response.ok) {
        const data = await response.json();
        setDailyCostData(data);
      }
    } catch (err) {
      console.error('Error fetching daily cost:', err);
    }
  };

  // Preload availability whenever options change
  useEffect(() => {
    preloadAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refereeOptions, benchOfficialOptions]);

  // Refetch daily cost whenever games change (assignments updated)
  useEffect(() => {
    if (games.length > 0) {
      fetchDailyCost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games]);

  const preloadAvailability = async () => {
    // Preload availability for all staff members
    if (refereeOptions.length === 0 && benchOfficialOptions.length === 0) return;

    const allPersonIds = new Set();
    refereeOptions.forEach(opt => allPersonIds.add(opt.personId));
    benchOfficialOptions.forEach(opt => allPersonIds.add(opt.personId));

    // Fire every per-person availability request in parallel via
    // Promise.all (was a serial for/await loop that took ~3s on a
    // 50-person roster). The browser will cap at ~6 concurrent
    // connections per host so this scales fine even with 100+ people;
    // the wall-clock end-to-end is the slowest single request, not the
    // sum of all of them.
    const ids = Array.from(allPersonIds);
    const results = await Promise.all(ids.map(async (personId) => {
      try {
        const res = await fetch(`${API_BASE}/person/${personId}/availability`);
        if (!res.ok) return [personId, null];
        return [personId, await res.json()];
      } catch (err) {
        console.error(`Error preloading availability for ${personId}:`, err);
        return [personId, null];
      }
    }));

    const newCache = { ...availabilityCache };
    for (const [personId, data] of results) {
      if (data) newCache[personId] = data;
    }
    setAvailabilityCache(newCache);
  };

  // Build the dropdown option array directly from the inline-qual rows
  // returned by /api/referees and /api/bench-officials. Both endpoints
  // now return one row per (person × qualification) with all the pay
  // columns flattened in, so we don't need a follow-up
  // /api/person/{id}/qualifications fetch per person — that loop was
  // serial and made the dropdowns take 5–10s to populate on a typical
  // 50-person roster.
  //
  // prefix: 'ref' or 'bench' — used as the key namespace in
  // qualificationDataMap so referee qual #5 doesn't collide with bench
  // qual #5.
  const buildOptionsFromRows = (rows, prefix) => {
    const options = [];
    const qualEntries = {};
    for (const r of rows) {
      if (!r.qualificationId) continue; // Defensive: row should always have a qual now.
      const key = `${prefix}_${r.qualificationId}`;
      // Stash the full qual row (with pay columns) under the namespaced
      // key. Backend returns them per-row so the same qual appears N
      // times for N people who hold it; we just overwrite — same data.
      qualEntries[key] = r;
      options.push({
        personId: r.id,
        qualificationId: key,
        label: `${r.name} - ${r.qualificationLevel}`,
        value: `${r.id}_${prefix}_${r.qualificationId}`,
        // Trainee-level options are shown grouped at the bottom of the
        // referee dropdown under a "Trainees" separator.
        isTrainee: /trainee/i.test(String(r.qualificationLevel || '')),
      });
    }
    return { options, qualEntries };
  };

  const fetchReferees = async () => {
    try {
      const response = await fetch(`${API_BASE}/referees`);
      if (!response.ok) return;
      const rows = await response.json();
      const { options, qualEntries } = buildOptionsFromRows(rows, 'ref');
      setRefereeOptions(options);
      setQualificationDataMap(prev => ({ ...prev, ...qualEntries }));
    } catch (err) {
      console.error('Error fetching referees:', err);
    }
  };

  const fetchBenchOfficials = async () => {
    try {
      const response = await fetch(`${API_BASE}/bench-officials`);
      if (!response.ok) return;
      const rows = await response.json();
      const { options, qualEntries } = buildOptionsFromRows(rows, 'bench');
      setBenchOfficialOptions(options);
      setQualificationDataMap(prev => ({ ...prev, ...qualEntries }));
    } catch (err) {
      console.error('Error fetching bench officials:', err);
    }
  };

  const fetchGamesByDate = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/games/by-day/${selectedDate}`);
      if (response.ok) {
        const data = await response.json();
        setGames(data.filter(g => g.gameStatus !== 'deleted'));
        setError('');
      }
    } catch (err) {
      setError('Failed to load games: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cycle a referee slot's code chip (CC → R → T → ... wrap to first).
  // Backend updates the GameAssignmentSlots row; we mirror the new code
  // into local state so the chip re-renders without a full game refresh.
  const cycleRefereeCode = async (gameId, personId) => {
    try {
      const res = await auditFetch(`${API_BASE}/games/${gameId}/slots/referee/${personId}/cycle-code`, {
        method: 'PUT'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Couldn't update code (HTTP ${res.status})`);
        return;
      }
      setGames(prev => prev.map(g => {
        if (g.id !== gameId) return g;
        return {
          ...g,
          referees: (g.referees || []).map(s =>
            s.personId === personId
              ? { ...s, refereeCodeId: data.refereeCodeId, refereeCode: data.code, refereeCodeLabel: data.label }
              : s
          )
        };
      }));
    } catch (err) {
      setError('Failed to cycle referee code: ' + err.message);
    }
  };

  // Cap on slots per role per game. Backend supports unbounded; UI exposes 5.
  const MAX_SLOTS_PER_ROLE = 5;

  // POST the full referees + benchOfficials arrays for a game.
  // Each entry: { personId, qualificationId? }. Order is significant (slotOrder = index + 1).
  const handleAssign = async (gameId, referees, benchOfficials) => {
    try {
      const response = await auditFetch(`${API_BASE}/games/${gameId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referees: referees.map(r => ({ personId: r.personId, qualificationId: r.qualificationId })),
          benchOfficials: benchOfficials.map(b => ({ personId: b.personId, qualificationId: b.qualificationId }))
        })
      });

      if (response.ok) {
        fetchGamesByDate();
      } else {
        setError('Failed to update assignments');
      }
    } catch (err) {
      setError('Error updating assignments: ' + err.message);
    }
  };

  // Slot-array helpers used by the render block.
  const updateRefSlots = (game, newRefs) => {
    handleAssignWithConfirm(game, game.id, newRefs, game.benchOfficials || []);
  };
  const updateBenchSlots = (game, newBench) => {
    handleAssignWithConfirm(game, game.id, game.referees || [], newBench);
  };

  // Helper function to convert UTC to local time based on offset and display
  // Uses originalScheduledTime unless the actual scheduledTime differs by 15+ minutes
  const getDisplayTime = (scheduledTime, originalScheduledTime) => {
    const utcDate = new Date(scheduledTime);
    const localDate = new Date(utcDate.getTime() + (gameTimeOffset * 60 * 60 * 1000));

    if (originalScheduledTime) {
      const originalUtc = new Date(originalScheduledTime);
      const originalLocal = new Date(originalUtc.getTime() + (gameTimeOffset * 60 * 60 * 1000));
      const diffMinutes = Math.abs(localDate.getTime() - originalLocal.getTime()) / (1000 * 60);

      // Show original time unless actual start differs by 15+ minutes
      if (diffMinutes < 15) {
        return originalLocal.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
      }
    }

    return localDate.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Get unique times and organize games by court and time
  const getTimeSlots = () => {
    const times = new Set();
    games.forEach(game => {
      const time = getDisplayTime(game.scheduledTime, game.originalScheduledTime);
      times.add(time);
    });
    return Array.from(times).sort();
  };

  const getGameForCourtAndTime = (court, time) => {
    return games.find(game => {
      const gameTime = getDisplayTime(game.scheduledTime, game.originalScheduledTime);
      return game.court === court && gameTime === time;
    });
  };

  // Get all person IDs assigned at a specific time slot on OTHER courts
  const getAssignedPeopleAtTimeSlotOnOtherCourts = (timeSlot, currentGameId) => {
    const assignedPersons = new Set();
    games.forEach(game => {
      const gameTime = getDisplayTime(game.scheduledTime, game.originalScheduledTime);
      if (gameTime === timeSlot && game.id !== currentGameId) {
        (game.referees || []).forEach(s => assignedPersons.add(s.personId));
        (game.benchOfficials || []).forEach(s => assignedPersons.add(s.personId));
      }
    });
    return assignedPersons;
  };

  // Time off (days off, or a period): is the game's hour inside it? Both sides are wall-clock times read as UTC,
  // the way scheduledTime is stored.
  const isOnTimeOffAt = (personId, scheduledTime) => {
    const list = availabilityCache[personId]?.timeOff;
    if (!list || list.length === 0) return false;
    const gameStartMs = new Date(scheduledTime).getTime();
    const gameEndMs = gameStartMs + 60 * 60 * 1000;
    return list.some(t => gameStartMs < new Date(`${t.endAt}Z`).getTime() && gameEndMs > new Date(`${t.startAt}Z`).getTime());
  };

  // Helper to check if person is available at game time
  const isPersonAvailableAtTime = (personId, scheduledTime) => {
    try {
      if (!availabilityCache[personId]) {
        // If not in cache, assume available
        return true;
      }
      if (isOnTimeOffAt(personId, scheduledTime)) return false;

      const availability = availabilityCache[personId];
      const gameDate = new Date(scheduledTime);
      const dayOfWeek = gameDate.getUTCDay();
      const dayAvailability = availability.availability?.[dayOfWeek];

      if (!dayAvailability) {
        // Default availability if not set
        return true;
      }

      if (!dayAvailability.isAvailable) {
        return false;
      }

      // Extract time from scheduledTime (e.g., "2026-02-06T16:20:00Z" -> "16:20")
      const time = new Date(scheduledTime).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
      const [hours, minutes] = time.split(':').map(Number);
      const requestMinutes = hours * 60 + minutes;
      const requestPlusOneHour = requestMinutes + 60;

      const [startHours, startMins] = dayAvailability.startTime.split(':').map(Number);
      const [endHours, endMins] = dayAvailability.endTime.split(':').map(Number);
      const startMinutes = startHours * 60 + startMins;
      const endMinutes = endHours * 60 + endMins;

      return requestMinutes >= startMinutes && requestPlusOneHour <= endMinutes;
    } catch (err) {
      console.error(`Error checking availability for ${personId}:`, err);
      return true; // Default to available if there's an error
    }
  };

  // Canonical age-group → pay-band resolvers. Mirrors the backend's
  // refBandForAgeGroup / benchBandForAgeGroup (server.js). Pay schema
  // collapsed 16 May 2026 to 6 referee bands + 2 bench bands.
  const _normAge = (ag) => String(ag || '').replace(/\s+/g, '').toLowerCase();
  const refBandCol = (ag) => {
    const a = _normAge(ag);
    if (!a) return null;
    if (a.startsWith('u10') || a.startsWith('u12') || a.startsWith('u14')) return 'payJuniors';
    if (a.startsWith('u16') || a.startsWith('u18')) return (a.includes('div2') || a.includes('div3')) ? 'payYouthDev' : 'payYouthChamp';
    if (a.startsWith('seniors')) return a.includes('champ') ? 'payChampSeniors' : 'paySeniors';
    if (a.startsWith('social')) return 'paySocial';
    return null;
  };
  const benchBandCol = (ag) => {
    const a = _normAge(ag);
    if (!a) return null;
    if (a.startsWith('seniors') || a.startsWith('social')) return 'paySeniorGames';
    if (a.startsWith('u10') || a.startsWith('u12') || a.startsWith('u14') || a.startsWith('u16') || a.startsWith('u18')) return 'payJuniorGames';
    return null;
  };

  // qualificationKey is a prefixed key like 'ref_1'
  const calculateRefereePayForQualification = (qualificationKey, division) => {
    const qual = qualificationDataMap[qualificationKey];
    if (!qual) return 0;
    const ageGroup = divisionMap[division] || 'Social Grade 1';
    const col = refBandCol(ageGroup);
    return (col && qual[col]) || 0;
  };

  // qualificationKey is a prefixed key like 'bench_1'. Falls back to a
  // legacy flat 'pay' field if the band column isn't populated.
  const calculateBenchOfficialPayForQualification = (qualificationKey, division) => {
    const qual = qualificationDataMap[qualificationKey];
    if (!qual) return 0;
    const ageGroup = divisionMap[division] || 'Social Grade 1';
    const col = benchBandCol(ageGroup);
    return (col && qual[col]) || qual.pay || 0;
  };

  // Filter dropdown options to exclude people already assigned in the same time slot on OTHER courts
  // AND people already assigned to other slots in the SAME game (but allow in their own slot).
  // currentSlot = { role, personId } | null   — the slot this dropdown represents
  //   (null means an "add new" dropdown — exclude everyone already in this game).
  // type: 'referee' or 'benchOfficial' - determines which pay calculation to use
  const getFilteredOptions = (options, timeSlot, currentGameId, currentGame, currentSlot, type = 'referee') => {
    const assignedPersons = getAssignedPeopleAtTimeSlotOnOtherCourts(timeSlot, currentGameId);

    if (currentGame) {
      const allSlots = [
        ...((currentGame.referees || []).map(s => ({ ...s, role: 'referee' }))),
        ...((currentGame.benchOfficials || []).map(s => ({ ...s, role: 'benchOfficial' })))
      ];
      for (const s of allSlots) {
        // Skip if this is the slot the dropdown represents (so we can still pick the same person there)
        if (currentSlot && currentSlot.role === s.role && currentSlot.personId === s.personId) continue;
        assignedPersons.add(s.personId);
      }
    }

    const filtered = options.filter(option => {
      const personId = option.value.split('_')[0];
      return !assignedPersons.has(parseInt(personId));
    });

    // Filter by qualification (only show people qualified for this division)
    // Only apply pay filtering to referees - bench officials show all with a bench qualification
    console.log(`Filtering ${type} for division: ${currentGame?.division}, divisionMap keys: ${Object.keys(divisionMap).length}`);
    let qualificationFiltered;
    if (type === 'referee') {
      qualificationFiltered = filtered.filter(opt => {
        // Trainees are always offered for manual placement regardless of
        // pay band — they shadow any division and their Trainee 1 qual
        // only has a juniors/youth-dev rate, so a pay>0 gate would hide
        // them on every senior/championship/social game. They're grouped
        // under the "Trainees" separator by renderAssignOptions.
        if (opt.isTrainee) return true;
        // Value format is now: personId_ref_qualId
        const parts = opt.value.split('_');
        const qualificationKey = parts.slice(1).join('_'); // e.g., 'ref_1'
        const pay = calculateRefereePayForQualification(qualificationKey, currentGame?.division);
        console.log(`  ${opt.label}: qualKey=${qualificationKey}, pay=${pay}`);
        return pay > 0; // Only show if they have non-zero pay for this division
      });
    } else {
      // Bench officials - filter by non-zero pay for this division (same as referees)
      qualificationFiltered = filtered.filter(opt => {
        const parts = opt.value.split('_');
        const qualificationKey = parts.slice(1).join('_'); // e.g., 'bench_1'
        const pay = calculateBenchOfficialPayForQualification(qualificationKey, currentGame?.division);
        console.log(`  ${opt.label}: qualKey=${qualificationKey}, pay=${pay}`);
        return pay > 0;
      });
    }
    console.log(`After qualification filter: ${qualificationFiltered.length} options remain`);

    // Sort alphabetically by name (label is "Name - Qualification" so a
    // straight localeCompare keeps each person's multiple qualifications
    // grouped together in order). Was previously cheapest-pay-first which
    // jumbled the names — operators want the dropdown to read like a
    // phone book so they can scan to the person they're after.
    qualificationFiltered.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    // Separate by availability using cached availability data and game time
    const availableByTime = qualificationFiltered.filter(opt => {
      const personId = parseInt(opt.value.split('_')[0]);
      return isPersonAvailableAtTime(personId, currentGame?.scheduledTime);
    });
    const unavailableByTime = qualificationFiltered.filter(opt => {
      const personId = parseInt(opt.value.split('_')[0]);
      return !isPersonAvailableAtTime(personId, currentGame?.scheduledTime);
    }).map(opt => (
      isOnTimeOffAt(parseInt(opt.value.split('_')[0]), currentGame?.scheduledTime)
        ? { ...opt, label: opt.label + ' (Time off)' }
        : opt
    ));

    // Check if person is playing at this timeslot (team assignment conflict)
    // Find all games at the same timeslot on this day
    const gamesAtTimeSlot = games.filter(g => {
      return getDisplayTime(g.scheduledTime, g.originalScheduledTime) === timeSlot;
    });
    // Build a set of team names playing at this timeslot (with their divisions)
    const teamsAtTimeSlot = [];
    gamesAtTimeSlot.forEach(g => {
      if (g.team1) teamsAtTimeSlot.push({ teamName: g.team1, division: g.division });
      if (g.team2) teamsAtTimeSlot.push({ teamName: g.team2, division: g.division });
    });

    // Build a map: personId -> list of teams they play for at this timeslot
    const personPlayingTeams = {};
    if (teamsAtTimeSlot.length > 0 && personTeamAssignments.length > 0) {
      personTeamAssignments.forEach(pta => {
        const isPlaying = teamsAtTimeSlot.some(
          t => t.teamName === pta.teamName && t.division === pta.division
        );
        if (isPlaying) {
          if (!personPlayingTeams[pta.personId]) personPlayingTeams[pta.personId] = [];
          personPlayingTeams[pta.personId].push(pta.teamName);
        }
      });
    }

    // Team Exclusions: people who must not be rostered on either team of THIS game.
    const excludedFromThisGame = new Set();
    if (currentGame && personTeamExclusions.length > 0) {
      personTeamExclusions.forEach(pte => {
        if (pte.division === currentGame.division && (pte.teamName === currentGame.team1 || pte.teamName === currentGame.team2)) {
          excludedFromThisGame.add(pte.personId);
        }
      });
    }
    const isExcluded = (opt) => excludedFromThisGame.has(parseInt(opt.value.split('_')[0]));

    // Split available into truly available and playing
    const available = availableByTime.filter(opt => {
      const personId = parseInt(opt.value.split('_')[0]);
      return !personPlayingTeams[personId] && !isExcluded(opt);
    });
    const excluded = availableByTime
      .filter(opt => isExcluded(opt))
      .map(opt => ({ ...opt, label: opt.label + ' (Excluded)' }));
    const playing = availableByTime
      .filter(opt => {
        const personId = parseInt(opt.value.split('_')[0]);
        return !!personPlayingTeams[personId] && !isExcluded(opt);
      })
      .map(opt => ({
        ...opt,
        label: opt.label + ' (Play)'
      }));
    const unavailable = [
      ...excluded,
      ...playing,
      ...unavailableByTime
    ];

    // Return both groups for rendering with separator
    return { available, unavailable };
  };

  // Render the <option> list for an assignment <select>. Non-trainee
  // options first; trainee options grouped at the bottom under a
  // "── Trainees ──" separator; then unavailable. Shared by the
  // rejected-slot replace select and the +Add select.
  const renderAssignOptions = (available, unavailable) => {
    const nonTrainee = available.filter(o => !o.isTrainee);
    const trainees = available.filter(o => o.isTrainee);
    return (
      <>
        {nonTrainee.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
        {trainees.length > 0 && <option disabled>── Trainees ──</option>}
        {trainees.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
        {unavailable.length > 0 && <option disabled>---Unavailable---</option>}
        {unavailable.map(option => (
          <option key={option.value} value={option.value} style={{ color: '#999' }}>{option.label}</option>
        ))}
      </>
    );
  };

  const timeSlots = getTimeSlots();

  return (
    <div className="game-planning">
      <div className="planning-controls">
        <div className="date-selector">
          <button
            className="btn-day-nav"
            onClick={() => {
              const [y, m, d] = selectedDate.split('-').map(Number);
              const date = new Date(y, m - 1, d - 1);
              const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              if (newDate >= minDateStr) setSelectedDate(newDate);
            }}
            disabled={selectedDate <= minDateStr}
          >&larr;</button>
          <label>Select Date:</label>
          <input
            type="date"
            value={selectedDate}
            min={minDateStr}
            max={maxDateStr}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <span style={{ fontWeight: 600, color: '#1565c0', fontSize: '0.9rem', marginLeft: 4 }}>
            {dowLong(selectedDate)}
          </span>
          <button
            className="btn-day-nav"
            onClick={() => {
              const [y, m, d] = selectedDate.split('-').map(Number);
              const date = new Date(y, m - 1, d + 1);
              const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              if (newDate <= maxDateStr) setSelectedDate(newDate);
            }}
            disabled={selectedDate >= maxDateStr}
          >&rarr;</button>
        </div>

        {!readOnly && (
          <button
            className={isDayLocked ? 'btn-locked' : 'btn-lock'}
            onClick={handleLockDay}
            disabled={isDayLocked || lockLoading}
          >
            {isDayLocked ? 'Day Locked' : lockLoading ? 'Locking...' : 'Lock Day'}
          </button>
        )}

        {!readOnly && (
          <button
            className="btn-daily-notes"
            onClick={() => { setDayNotesDraft(dayNotes); setDayNotesError(''); setShowDayNotesModal(true); }}
            title={dayNotes.trim() ? dayNotes : 'No daily notes yet — click to add a day-level General Info comment'}
            style={{
              background: dayNotes.trim() ? '#2e7d32' : '#9e9e9e',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            Daily Notes
          </button>
        )}

        {canAiAllocate && (
          <button
            className="btn-auto-allocate"
            onClick={handleOpenAllocate}
            style={{
              background: 'linear-gradient(135deg, #1565c0, #0d47a1)',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            AI Allocate
          </button>
        )}
        {canImportRoster && (
          <button
            className="btn-import-roster"
            onClick={() => { setImportPreview(null); setImportError(''); setShowImportModal(true); }}
            style={{
              background: '#37474f',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
            title="Upload an external roster (image or PDF) and apply its assignments"
          >
            Import Roster
          </button>
        )}
        {canAllAttended && (
          <button
            className="btn-all-attended"
            onClick={handleAllAttended}
            disabled={allAttendedLoading || isDayLocked}
            style={{
              background: allAttendedLoading || isDayLocked ? '#bdbdbd' : '#2e7d32',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: allAttendedLoading || isDayLocked ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
            title={isDayLocked ? 'Day is locked' : 'Mark every referee + bench official assignment on this day as attended'}
          >
            {allAttendedLoading ? 'Marking…' : 'All Attended'}
          </button>
        )}
        {canClearRoster && (
          <button
            className="btn-clear-roster"
            onClick={handleClearRoster}
            disabled={clearRosterLoading || isDayLocked}
            style={{
              background: clearRosterLoading || isDayLocked ? '#bdbdbd' : '#c62828',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: clearRosterLoading || isDayLocked ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
            title={isDayLocked ? 'Day is locked' : 'Delete every referee + bench official assignment for this day, including attendance ticks. Cannot be undone.'}
          >
            {clearRosterLoading ? 'Clearing…' : 'Clear Roster'}
          </button>
        )}
      </div>

      {/* Budget Bar — ALWAYS rendered so the page never reflows when
          a refetch is in flight. The container exists from first paint
          on; the figures inside swap in/out as data arrives. Earlier
          version made the whole bar conditional on dailyCostData, which
          let it disappear (and the page jump) any time the post-click
          recalc was slow or briefly nulled out the state. */}
      <div className="budget-bar">
        <span className="budget-bar-item">
          Daily Cost: <strong>{dailyCostData ? `$${dailyCostData.totalDailyCost.toFixed(2)}` : '—'}</strong>
        </span>
        <span className="budget-bar-item">
          Budget: <strong>{dailyCostData?.budget ? `$${dailyCostData.budget.budgetAmount.toFixed(2)}` : '—'}</strong>
        </span>
        {dailyCostData?.isOverBudget && dailyCostData?.budget ? (
          <span className="budget-bar-status budget-bar-over">⚠️ Over Budget by ${dailyCostData.overage.toFixed(2)}</span>
        ) : dailyCostData?.budget ? (
          <span className="budget-bar-status budget-bar-ok">✓ Under Budget</span>
        ) : (
          <span className="budget-bar-status" style={{ color: '#999' }}>{dailyCostData ? 'No budget set' : ''}</span>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Only show the loading banner on the first fetch (when the grid
          isn't rendered yet). On subsequent refetches — e.g. after every
          assignment — keep the existing grid mounted so the page doesn't
          jump up and back down on each click. */}
      {loading && games.length === 0 && <div className="loading">Loading games...</div>}

      {games.length === 0 ? (
        <div className="no-games">No games scheduled for this date</div>
      ) : (
        <div className="schedule-grid">
          <div className="schedule-header" style={{ gridTemplateColumns: gridTemplateColumnsStyle }}>
            <div className="time-column-header"></div>
            {COURTS.map(court => (
              <div key={court} className="court-header">
                {court}
              </div>
            ))}
          </div>

          {timeSlots.map(time => (
            <div key={time} className="schedule-row" style={{ gridTemplateColumns: gridTemplateColumnsStyle }}>
              <div className="time-slot">{time}</div>
              {COURTS.map(court => {
                const game = getGameForCourtAndTime(court, time);
                return (
                  <div key={`${court}-${time}`} className="court-slot">
                    {game ? (
                      <div
                        className="game-slot"
                        style={{
                          backgroundColor: game.gameStatus === 'changed' ? '#ffe6cc' : '#ffffff'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div className="game-title">
                              {(() => {
                                // Bold the winning team once both scores are
                                // in (i.e. game is complete). Tied games and
                                // games still missing a score render normally.
                                const s1 = game.team1Score;
                                const s2 = game.team2Score;
                                const bothScored = s1 != null && s2 != null;
                                const team1Wins = bothScored && s1 > s2;
                                const team2Wins = bothScored && s2 > s1;
                                const winnerStyle = { fontWeight: 800 };
                                const loserStyle = { fontWeight: 400 };
                                const team1Style = team1Wins ? winnerStyle : (team2Wins ? loserStyle : null);
                                const team2Style = team2Wins ? winnerStyle : (team1Wins ? loserStyle : null);
                                return (
                                  <>
                                    <span style={team1Style}>
                                      {game.team1}{s1 != null ? ` (${s1})` : ''}
                                    </span>
                                    {' vs '}
                                    <span style={team2Style}>
                                      {game.team2}{s2 != null ? ` (${s2})` : ''}
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                            <div className="game-info">
                              {game.division}
                            </div>
                          </div>
                          {(() => {
                            // Court-supervisor Report button: grey until the
                            // game has stored report content, then green.
                            // Native title tooltip shows what's stored.
                            const rep = gameReports[game.id];
                            const filled = reportHasContent(rep);
                            const suspended = gameSuspensions[game.id] || [];
                            return (
                              <>
                              {suspended.length > 0 && (
                                <button
                                  className="btn-game-suspension"
                                  title={suspended.map(s => `${s.playerName} (${s.team})`).join('\n')}
                                  onClick={() => setSuspensionModalGame(game)}
                                  style={{
                                    background: '#c62828', color: 'white', border: 'none', borderRadius: 4,
                                    padding: '2px 0', width: 104, fontSize: '0.68rem', fontWeight: 700,
                                    cursor: 'pointer', flexShrink: 0, marginLeft: 4,
                                  }}
                                >
                                  Suspension{suspended.length > 1 ? ` (${suspended.length})` : ''}
                                </button>
                              )}
                              <button
                                className="btn-game-report"
                                title={reportSummary(rep)}
                                onClick={() => openReportModal(game)}
                                style={{
                                  background: filled ? '#2e7d32' : '#9e9e9e',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  padding: '2px 0',
                                  width: 52,
                                  fontSize: '0.68rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  flexShrink: 0,
                                  marginLeft: 4,
                                }}
                              >
                                Report
                              </button>
                              </>
                            );
                          })()}
                        </div>
                        <div className="selectors-container">
                          {(() => {
                            // Single render block for both referees and bench officials —
                            // all the per-slot logic is now array-based, so we share it.
                            const renderRoleSection = (role, options, type, addLabel) => {
                              const arrayKey = role === 'referee' ? 'referees' : 'benchOfficials';
                              const slots = game[arrayKey] || [];
                              const slotLabel = (slot) => {
                                const opt = options.find(o => parseInt(o.value.split('_')[0]) === slot.personId);
                                if (opt) return opt.label;
                                return slot.personName || `${role === 'referee' ? 'Referee' : 'Official'} #${slot.personId}`;
                              };
                              const valueFor = (slot) => slot.qualificationId
                                ? `${slot.personId}_${type === 'referee' ? 'ref' : 'bench'}_${slot.qualificationId}`
                                : `${slot.personId}_null`;
                              const setSlots = role === 'referee' ? updateRefSlots : updateBenchSlots;
                              const replaceSlot = (slotOrder, e) => {
                                const parts = e.target.value.split('_');
                                const personId = parts[0] ? parseInt(parts[0]) : null;
                                const qualificationId = parts.length >= 3 ? parseInt(parts[2]) : null;
                                if (!personId) return;
                                const newSlots = slots.map(s =>
                                  s.slotOrder === slotOrder
                                    ? { personId, qualificationId, slotOrder }
                                    : s
                                );
                                setSlots(game, newSlots);
                              };
                              const removeSlot = (personIdToRemove) => {
                                setSlots(game, slots.filter(s => s.personId !== personIdToRemove));
                              };
                              const addSlot = (e) => {
                                const parts = e.target.value.split('_');
                                const personId = parts[0] ? parseInt(parts[0]) : null;
                                const qualificationId = parts.length >= 3 ? parseInt(parts[2]) : null;
                                if (!personId) return;
                                setSlots(game, [...slots, { personId, qualificationId }]);
                              };
                              const canAddMore = !readOnly && !isDayLocked && slots.length < MAX_SLOTS_PER_ROLE;

                              // Referee slot role chip. Each existing referee
                              // slot carries its own refereeCode (set by the
                              // backend defaulting to the code at displayOrder
                              // = slotOrder; cycled by this click handler).
                              // Bench officials get no chip.
                              //
                              // For an EXISTING slot (slot != null with personId),
                              // the chip is a button that POSTs the cycle-code
                              // endpoint and updates local state.
                              //
                              // For the +Add placeholder (no person yet), we
                              // render a non-clickable chip preview using the
                              // slotOrder→{CC,R,T} default so the assigner can
                              // see which seat they're filling. The actual code
                              // is decided server-side at INSERT time.
                              const refSlotChip = (slot, slotOrder) => {
                                if (role !== 'referee') return null;
                                const isExistingSlot = slot && slot.personId;
                                const text = isExistingSlot
                                  ? (slot.refereeCode || '')
                                  : ({ 1: 'CC', 2: 'R', 3: 'T' }[slotOrder] || '');
                                const label = isExistingSlot
                                  ? (slot.refereeCodeLabel || 'Click to cycle')
                                  : ({ 1: 'Crew Chief', 2: 'Referee', 3: 'Trainee' }[slotOrder] || '');
                                const baseStyle = {
                                  display: 'inline-block',
                                  minWidth: 26,
                                  padding: '2px 6px',
                                  marginRight: 4,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  color: '#37474f',
                                  background: '#eceff1',
                                  border: '1px solid #b0bec5',
                                  borderRadius: 3,
                                  textAlign: 'center',
                                  lineHeight: '1.4'
                                };
                                if (!isExistingSlot || readOnly || isDayLocked) {
                                  return <span style={baseStyle} title={label}>{text || '—'}</span>;
                                }
                                return (
                                  <button
                                    type="button"
                                    onClick={() => cycleRefereeCode(game.id, slot.personId)}
                                    style={{ ...baseStyle, cursor: 'pointer' }}
                                    title={`${label} — click to cycle`}
                                  >{text || '—'}</button>
                                );
                              };
                              return (
                                <div className="selector-group">
                                  <label className="selector-label">{role === 'referee' ? 'Referees' : 'Bench Officials'}</label>
                                  <div className="referee-selectors">
                                    {slots.map(slot => {
                                      const slotValue = valueFor(slot);
                                      const isAttended = slot.attended === 1 || slot.attended === true;
                                      const editable = slot.response === 'rejected' && !readOnly && !isDayLocked;
                                      const currentSlotForFilter = { role, personId: slot.personId };
                                      // $0-cost flag: the slot resolves to no pay (no
                                      // qualification, or the qualification has no rate
                                      // for this game's band). Light-blue tint so the
                                      // operator can spot a likely mis-assignment that
                                      // would silently cost nothing.
                                      const zeroPay = slot.personId && (slot.pay == null || Number(slot.pay) <= 0);
                                      const zeroPayTitle = zeroPay ? 'This assignment resolves to $0 — the person has no qualification, or no pay rate for this division. Check Admin → Qualifications or pick a different person/qual.' : undefined;
                                      return (
                                        <div key={`${role}-${slot.slotOrder}-${slot.personId}`} className="select-with-attendance">
                                          {refSlotChip(slot, slot.slotOrder)}
                                          {editable ? (
                                            <select
                                              className="ref-select"
                                              value={slotValue}
                                              style={{ background: '#ffcdd2' }}
                                              onChange={(e) => replaceSlot(slot.slotOrder, e)}
                                            >
                                              <option value={slotValue} style={{ color: '#c62828' }}>{slotLabel(slot)} (Rejected)</option>
                                              {(() => {
                                                const { available, unavailable } = getFilteredOptions(options, getDisplayTime(game.scheduledTime, game.originalScheduledTime), game.id, game, currentSlotForFilter, type);
                                                return renderAssignOptions(available, unavailable);
                                              })()}
                                            </select>
                                          ) : (
                                            <select className="ref-select" disabled value={slotValue} title={zeroPayTitle} style={{ background: zeroPay ? '#cfe8ff' : (slot.response === 'accepted' ? '#a5d6a7' : '#f0f0f0') }}>
                                              <option value={slotValue}>{slotLabel(slot)}{zeroPay ? ' • $0' : ''}</option>
                                            </select>
                                          )}
                                          <input
                                            type="checkbox"
                                            className="attendance-tick"
                                            checked={isAttended}
                                            onChange={() => handleAttendanceToggle(game.id, slot.personId, role, slot.attended)}
                                            disabled={isDayLocked || readOnly || !isGameTimePassed(game.scheduledTime)}
                                            title={!isGameTimePassed(game.scheduledTime) ? 'Available after game time' : 'Mark attended'}
                                          />
                                          {!readOnly && !isDayLocked && !isAttended && (
                                            <button className="assignment-remove-btn" onClick={() => removeSlot(slot.personId)} title="Remove">&#10005;</button>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {canAddMore && (
                                      <div className="select-with-attendance">
                                        {refSlotChip(null, slots.length + 1)}
                                        <select className="ref-select" value="" onChange={addSlot}>
                                          <option value="">{addLabel}</option>
                                          {(() => {
                                            const { available, unavailable } = getFilteredOptions(options, getDisplayTime(game.scheduledTime, game.originalScheduledTime), game.id, game, null, type);
                                            return renderAssignOptions(available, unavailable);
                                          })()}
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            };
                            return (
                              <>
                                {renderRoleSection('referee', refereeOptions, 'referee', '+ Assign Referee')}
                                {renderRoleSection('benchOfficial', benchOfficialOptions, 'benchOfficial', '+ Assign Official')}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="empty-slot"></div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Auto-Allocation Modal */}
      {showAllocateModal && (
        <div className="modal-overlay" onClick={() => !allocateLoading && !applyingAllocation && setShowAllocateModal(false)}>
          <div className="modal-content" style={{ maxWidth: '700px', maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, color: '#1565c0' }}>AI Roster Allocation</h2>
              <button className="modal-close" onClick={() => !allocateLoading && !applyingAllocation && setShowAllocateModal(false)}>&times;</button>
            </div>

            {!allocateResults ? (
              <div style={{ padding: '1.5rem 0' }}>
                <p style={{ color: '#555', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  Automatically allocate <strong>2 referees</strong> and <strong>1 bench official</strong> per game based on availability, historical patterns, cost efficiency, and fairness. Championship games get the configured championship crew size; <strong>Social games get a single referee (Crew Chief)</strong>.
                </p>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.85rem' }}>Start Date</label>
                    <input type="date" value={allocateStartDate} onChange={e => setAllocateStartDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }} />
                    <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(allocateStartDate)}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.85rem' }}>End Date</label>
                    <input type="date" value={allocateEndDate} onChange={e => setAllocateEndDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }} />
                    <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(allocateEndDate)}</div>
                  </div>
                </div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.85rem', color: '#37474f' }}>
                  Compare against (the "Last Period" baseline)
                </label>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.8rem', color: '#666' }}>Comparison Start</label>
                    <input type="date" value={allocateCompStartDate} onChange={e => setAllocateCompStartDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }} />
                    <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(allocateCompStartDate)}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.8rem', color: '#666' }}>Comparison End</label>
                    <input type="date" value={allocateCompEndDate} onChange={e => setAllocateCompEndDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }} />
                    <div style={{ fontSize: '0.78rem', color: '#1565c0', fontWeight: 600, marginTop: 2 }}>{dowLong(allocateCompEndDate)}</div>
                  </div>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const shift = (v) => { if (!v) return ''; const d = new Date(v); d.setDate(d.getDate() - 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
                      setAllocateCompStartDate(shift(allocateStartDate));
                      setAllocateCompEndDate(shift(allocateEndDate));
                    }}
                    style={{ background: 'none', border: 'none', color: '#1565c0', fontSize: '0.78rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    Reset to the week before the proposed range
                  </button>
                </div>
                <div style={{ background: '#e3f2fd', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem', fontSize: '0.85rem', color: '#1565c0' }}>
                  <strong>How it works:</strong> Games are scored by importance (seniors first). Referees are matched by qualification, availability, historical affinity, and cost efficiency. Fairness penalties prevent uneven distribution. Locked days and confirmed attendance are skipped. The per-day breakdown pairs each proposed day with the comparison day in the same position of the comparison range.
                </div>
                <button
                  onClick={handleRunAllocation}
                  disabled={allocateLoading || !allocateStartDate || !allocateEndDate}
                  style={{
                    width: '100%', background: 'linear-gradient(135deg, #1565c0, #0d47a1)', color: 'white',
                    border: 'none', padding: '0.75rem', borderRadius: '8px', fontWeight: 600, fontSize: '1rem',
                    cursor: allocateLoading ? 'wait' : 'pointer', opacity: allocateLoading ? 0.7 : 1
                  }}
                >
                  {allocateLoading ? 'Calculating allocation...' : 'Run Allocation'}
                </button>
              </div>
            ) : (
              <div style={{ padding: '1rem 0' }}>
                {/* COST COMPARISON STRIP — three big numbers side-by-side
                    so the operator can sanity-check the proposed run
                    against history + budget before clicking Apply. The
                    Proposed cell colours red when it exceeds the budget,
                    matching the Game Planner Daily Cost bar's convention.
                    Returned by the backend in allocateResults.costSummary. */}
                {allocateResults.costSummary && (() => {
                  const cs = allocateResults.costSummary;
                  const overBudget = cs.budgetForRange > 0 && cs.proposedCost > cs.budgetForRange;
                  // Helper: pretty-print a (slots, games) line with the
                  // implied $/slot rate so the operator can spot whether
                  // a big period-over-period delta is a volume issue
                  // (different slot count) or a rate issue (different
                  // $/slot). Hides when there are no slots.
                  const ratePerSlot = (cost, slots) => slots > 0 ? `$${(cost / slots).toFixed(2)}/slot` : '';
                  const cell = (label, value, sublabel, bg, fg, slots, games) => (
                    <div style={{ flex: 1, background: bg, borderRadius: 8, padding: '0.75rem', textAlign: 'center', minWidth: 160 }}>
                      <div style={{ fontSize: '0.7rem', color: fg, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: fg, marginTop: 2 }}>${value.toFixed(2)}</div>
                      {sublabel && <div style={{ fontSize: '0.7rem', color: fg, opacity: 0.85, marginTop: 2 }}>{sublabel}</div>}
                      {(slots != null || games != null) && (
                        <div style={{ fontSize: '0.7rem', color: fg, opacity: 0.85, marginTop: 4, borderTop: `1px dashed ${fg}33`, paddingTop: 4 }}>
                          {games != null && <>{games} game{games === 1 ? '' : 's'}</>}{slots != null && <>{games != null ? ' · ' : ''}{slots} slot{slots === 1 ? '' : 's'}</>}{slots > 0 && <> · {ratePerSlot(value, slots)}</>}
                        </div>
                      )}
                    </div>
                  );
                  return (
                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      {cell('Last Period', cs.lastPeriod.actualCost, `${cs.lastPeriod.startDate} → ${cs.lastPeriod.endDate}${cs.lastPeriod.zeroPaySlots > 0 ? ` · ⚠ ${cs.lastPeriod.zeroPaySlots} $0 slots` : ''}`, '#eceff1', '#37474f', cs.lastPeriod.slotCount, cs.lastPeriod.gameCount)}
                      {cell('Budget',       cs.budgetForRange,       `${cs.rangeDays} day${cs.rangeDays === 1 ? '' : 's'}`,           '#e3f2fd', '#1565c0', null, null)}
                      {cell('Proposed',     cs.proposedCost,         overBudget ? `Over budget by $${(cs.proposedCost - cs.budgetForRange).toFixed(2)}` : (cs.budgetForRange > 0 ? '✓ Under budget' : 'No budget set'), overBudget ? '#ffebee' : '#e8f5e9', overBudget ? '#c62828' : '#2e7d32', cs.proposedSlotCount, cs.proposedGameCount)}
                    </div>
                  );
                })()}

                {/* Counts strip — kept as a thinner row so the cost block
                    reads first. Unfilled / Skipped only show when non-zero. */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                  <div style={{ flex: 1, background: '#f5f5f5', borderRadius: 8, padding: '0.55rem 0.75rem', textAlign: 'center', minWidth: 110 }}>
                    <strong style={{ color: '#2e7d32' }}>{allocateResults.totalGames}</strong> games allocated
                  </div>
                  {allocateResults.unfilledSlots > 0 && (
                    <div style={{ flex: 1, background: '#ffebee', borderRadius: 8, padding: '0.55rem 0.75rem', textAlign: 'center', minWidth: 110 }}>
                      <strong style={{ color: '#c62828' }}>{allocateResults.unfilledSlots}</strong> games unfilled
                    </div>
                  )}
                  {allocateResults.skippedGames > 0 && (
                    <div style={{ flex: 1, background: '#fff3e0', borderRadius: 8, padding: '0.55rem 0.75rem', textAlign: 'center', minWidth: 110 }}>
                      <strong style={{ color: '#e65100' }}>{allocateResults.skippedGames}</strong> games skipped (locked / attended)
                    </div>
                  )}
                </div>

                {/* PER-DAY BREAKDOWN vs same day last week. Collapsible
                    because it's a debug tool — most allocations don't
                    need it, but when the high-level Last Period vs
                    Proposed delta surprises the operator they expand
                    this to find the exact day(s) driving the difference.
                    Each row pairs a proposed day with the SAME day-of-
                    week from the previous week (Mon ↔ Mon) so the
                    comparison is apples-to-apples; the existing Last
                    Period cell at the top uses immediately-before, which
                    is misleading when the proposed range is mostly
                    weekdays and "immediately before" includes the
                    weekend (or vice versa). */}
                {Array.isArray(allocateResults.costSummary?.dailyComparison) && allocateResults.costSummary.dailyComparison.length > 0 && (
                  <details style={{ marginBottom: '1rem', border: '1px solid #ddd', borderRadius: 6, padding: '0.4rem 0.75rem' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#1565c0', fontWeight: 600 }}>
                      Side-by-side per-day breakdown vs comparison period
                    </summary>
                    <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead style={{ background: '#eceff1' }}>
                          <tr>
                            <th rowSpan={2} style={{ padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid #ccc' }}>Day</th>
                            <th colSpan={5} style={{ padding: '0.4rem', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #ccc' }}>Comparison Period</th>
                            <th colSpan={4} style={{ padding: '0.4rem', textAlign: 'center', background: '#e3f2fd', borderBottom: '1px solid #ccc' }}>Proposed</th>
                            <th rowSpan={2} style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Δ Cost</th>
                            <th rowSpan={2} style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Δ Slots</th>
                          </tr>
                          <tr>
                            <th style={{ padding: '0.3rem', textAlign: 'left', background: '#fafafa', fontSize: '0.7rem', color: '#666' }}>Date</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right', background: '#fafafa', fontSize: '0.7rem' }}>Cost</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right', background: '#fafafa', fontSize: '0.7rem' }}>Slots</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right', background: '#fafafa', fontSize: '0.7rem' }}>Games</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right', background: '#fff8e1', fontSize: '0.7rem' }} title="Slots assigned to someone but resolving to $0 (wrong qual selected, or qual has no rate for this division)">$0 Slots</th>
                            <th style={{ padding: '0.3rem', textAlign: 'left', background: '#e3f2fd', fontSize: '0.7rem', color: '#666' }}>Date</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right', background: '#e3f2fd', fontSize: '0.7rem' }}>Cost</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right', background: '#e3f2fd', fontSize: '0.7rem' }}>Slots</th>
                            <th style={{ padding: '0.3rem', textAlign: 'right', background: '#e3f2fd', fontSize: '0.7rem' }}>Games</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allocateResults.costSummary.dailyComparison.map(d => {
                            const dCost = (d.proposed.cost - d.lastWeek.cost);
                            const dSlots = d.proposed.slots - d.lastWeek.slots;
                            const dCostColor = dCost > 0 ? '#c62828' : (dCost < 0 ? '#2e7d32' : '#666');
                            const dSlotsColor = dSlots > 0 ? '#c62828' : (dSlots < 0 ? '#2e7d32' : '#666');
                            return (
                              <tr key={d.proposedDate} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.35rem', fontWeight: 600 }}>{d.dayOfWeek}</td>
                                <td style={{ padding: '0.35rem', color: '#666', fontSize: '0.75rem' }}>{d.lastWeekDate}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right' }}>${d.lastWeek.cost.toFixed(2)}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right' }}>{d.lastWeek.slots}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right' }}>{d.lastWeek.games}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right', background: d.lastWeek.zeroPaySlots > 0 ? '#fff8e1' : undefined, color: d.lastWeek.zeroPaySlots > 0 ? '#e65100' : '#666', fontWeight: d.lastWeek.zeroPaySlots > 0 ? 700 : 400 }}>{d.lastWeek.zeroPaySlots || 0}</td>
                                <td style={{ padding: '0.35rem', color: '#666', fontSize: '0.75rem' }}>{d.proposedDate}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right' }}>${d.proposed.cost.toFixed(2)}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right' }}>{d.proposed.slots}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right' }}>{d.proposed.games}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right', color: dCostColor, fontWeight: 700 }}>{dCost > 0 ? '+' : ''}${dCost.toFixed(2)}</td>
                                <td style={{ padding: '0.35rem', textAlign: 'right', color: dSlotsColor, fontWeight: 700 }}>{dSlots > 0 ? '+' : ''}{dSlots}</td>
                              </tr>
                            );
                          })}
                          {/* Totals row */}
                          {(() => {
                            const totals = allocateResults.costSummary.dailyComparison.reduce((acc, d) => ({
                              lastCost: acc.lastCost + d.lastWeek.cost,
                              lastSlots: acc.lastSlots + d.lastWeek.slots,
                              lastGames: acc.lastGames + d.lastWeek.games,
                              lastZero: acc.lastZero + (d.lastWeek.zeroPaySlots || 0),
                              propCost: acc.propCost + d.proposed.cost,
                              propSlots: acc.propSlots + d.proposed.slots,
                              propGames: acc.propGames + d.proposed.games,
                            }), { lastCost: 0, lastSlots: 0, lastGames: 0, lastZero: 0, propCost: 0, propSlots: 0, propGames: 0 });
                            const dCost = totals.propCost - totals.lastCost;
                            const dSlots = totals.propSlots - totals.lastSlots;
                            return (
                              <tr style={{ background: '#fff8e1', fontWeight: 700, borderTop: '2px solid #999' }}>
                                <td style={{ padding: '0.4rem' }}>TOTAL</td>
                                <td></td>
                                <td style={{ padding: '0.4rem', textAlign: 'right' }}>${totals.lastCost.toFixed(2)}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{totals.lastSlots}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{totals.lastGames}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'right', color: totals.lastZero > 0 ? '#e65100' : '#666' }}>{totals.lastZero}</td>
                                <td></td>
                                <td style={{ padding: '0.4rem', textAlign: 'right' }}>${totals.propCost.toFixed(2)}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{totals.propSlots}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{totals.propGames}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'right', color: dCost > 0 ? '#c62828' : '#2e7d32' }}>{dCost > 0 ? '+' : ''}${dCost.toFixed(2)}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'right', color: dSlots > 0 ? '#c62828' : '#2e7d32' }}>{dSlots > 0 ? '+' : ''}{dSlots}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                      <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#666', lineHeight: 1.5 }}>
                        <strong>How to read this:</strong> Each row pairs a proposed day with the comparison day in the same position of the comparison range. <em>Δ Slots</em> tells you whether the difference is volume. <em>Δ Cost</em> compared to <em>Δ Slots</em> tells you whether the per-slot rate also shifted (different qualifications being assigned).
                      </p>
                      {/* Zero-pay callout — most common cause of "AI is
                          more expensive than last week even with fewer
                          slots". When last week has zero-pay slots, the
                          actual cost is artificially suppressed. */}
                      {(() => {
                        const totalZero = (allocateResults.costSummary.dailyComparison || []).reduce((s, d) => s + (d.lastWeek.zeroPaySlots || 0), 0);
                        if (totalZero === 0) return null;
                        const details = allocateResults.costSummary.lastPeriod?.zeroPayDetails || [];
                        const truncated = !!allocateResults.costSummary.lastPeriod?.zeroPayDetailsTruncated;
                        return (
                          <div style={{ marginTop: '0.6rem', padding: '0.6rem 0.8rem', background: '#fff8e1', borderLeft: '4px solid #e65100', borderRadius: 4, fontSize: '0.78rem', color: '#5d4037', lineHeight: 1.5 }}>
                            <strong style={{ color: '#e65100' }}>⚠ {totalZero} comparison-period slot{totalZero === 1 ? '' : 's'} resolved to $0.</strong>{' '}
                            Most likely cause of "AI Allocate looks more expensive than the comparison period even with fewer slots". Those slots had someone assigned but the qualification on file pays $0 for that game's division — so they contribute nothing to the comparison period's actual, while AI Allocate fills every slot at a real non-zero rate. Two fixes:
                            <ul style={{ margin: '0.4rem 0 0 1.1rem', padding: 0 }}>
                              <li>Open <strong>Admin &rarr; Qualifications</strong> and check that each qualification has a pay rate set for every age group it should cover. Empty cells = $0 for that division.</li>
                              <li>On the Game Planner, hover the dropdown to see what qualification each person is currently assigned at — picking the wrong one can also trigger $0 pay.</li>
                            </ul>

                            {/* Per-slot details — capped at 100 in the
                                backend; rendered inside a <details>
                                so the callout stays compact when the
                                operator isn't actively investigating. */}
                            {details.length > 0 && (
                              <details style={{ marginTop: '0.6rem' }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 700, color: '#e65100' }}>
                                  Show the {totalZero} affected slot{totalZero === 1 ? '' : 's'}{truncated ? ` (showing first ${details.length})` : ''}
                                </summary>
                                <div style={{ marginTop: '0.4rem', maxHeight: 320, overflowY: 'auto', border: '1px solid #ffcc80', borderRadius: 4, background: 'white' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', color: '#333' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#ffe0b2' }}>
                                      <tr>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Date</th>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Time</th>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Court</th>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Division</th>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Person</th>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Role</th>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Qualification</th>
                                        <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left' }}>Why $0</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {details.map((d, i) => (
                                        <tr key={`${d.personId}-${d.date}-${d.time}-${i}`} style={{ borderBottom: '1px solid #f5e9d3' }}>
                                          <td style={{ padding: '0.3rem 0.5rem', whiteSpace: 'nowrap' }}>{d.date}</td>
                                          <td style={{ padding: '0.3rem 0.5rem', whiteSpace: 'nowrap' }}>{d.time}</td>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{d.court}</td>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{d.division || '—'}</td>
                                          <td style={{ padding: '0.3rem 0.5rem', fontWeight: 600 }}>{d.personName}</td>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{d.role === 'referee' ? 'Referee' : 'Bench'}</td>
                                          <td style={{ padding: '0.3rem 0.5rem' }}>{d.qualificationLevel || <em style={{ color: '#c62828' }}>(none)</em>}</td>
                                          <td style={{ padding: '0.3rem 0.5rem', color: '#5d4037' }}>{d.reason}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </details>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </details>
                )}

                {/* Cannot-make list — every slot the allocator
                    couldn't fill, with the dominant rejection reason
                    so the admin can act on it (e.g. "T (slot 3) -
                    8 already on another game in this slot" → not
                    enough trainees in the pool for this many courts;
                    "Bench - 4 unavailable per their availability
                    window" → check the bench-official availability). */}
                {Array.isArray(allocateResults.cannotMake) && allocateResults.cannotMake.length > 0 && (
                  <>
                    <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem', color: '#c62828' }}>
                      Assignments I couldn't make ({allocateResults.cannotMake.length})
                    </h3>
                    <div style={{ overflowX: 'auto', marginBottom: '1.25rem', maxHeight: '260px', overflowY: 'auto', border: '1px solid #ef9a9a', borderRadius: 6 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#ffebee' }}>
                          <tr style={{ textAlign: 'left' }}>
                            <th style={{ padding: '0.4rem 0.5rem' }}>Time</th>
                            <th style={{ padding: '0.4rem 0.5rem' }}>Court</th>
                            <th style={{ padding: '0.4rem 0.5rem' }}>Division</th>
                            <th style={{ padding: '0.4rem 0.5rem' }}>Matchup</th>
                            <th style={{ padding: '0.4rem 0.5rem' }}>Slot</th>
                            <th style={{ padding: '0.4rem 0.5rem' }}>Why</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allocateResults.cannotMake.map((cm, i) => (
                            <tr key={`${cm.gameId}-${cm.slot}-${i}`} style={{ borderBottom: '1px solid #fce4ec' }}>
                              <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{cm.time}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{cm.court}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{cm.division || '—'}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{cm.matchup}</td>
                              <td style={{ padding: '0.35rem 0.5rem', fontWeight: 700, color: '#c62828' }}>{cm.slot}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{cm.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* TAB TOGGLE — Per Person / Per Game. The Per Person tab
                    is the existing fairness table; Per Game lists the
                    proposed crew on each individual game so the operator
                    can sanity-check assignments before clicking Apply.
                    Export buttons (CSV / PDF) on the right export the
                    visible tab's data. */}
                <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #ddd', marginBottom: '0.75rem', alignItems: 'flex-end' }}>
                  {[
                    { key: 'person', label: 'Per Person' },
                    { key: 'game',   label: 'Per Game' },
                  ].map(t => (
                    <button
                      key={t.key}
                      onClick={() => setAllocateView(t.key)}
                      style={{
                        background: 'none', border: 'none',
                        padding: '0.5rem 1rem', cursor: 'pointer',
                        borderBottom: allocateView === t.key ? '3px solid #1565c0' : '3px solid transparent',
                        color: allocateView === t.key ? '#1565c0' : '#666',
                        fontWeight: allocateView === t.key ? 700 : 500,
                        fontSize: '0.95rem',
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', paddingBottom: '0.4rem' }}>
                    <button
                      onClick={allocateView === 'game' ? exportPerGameCsv : exportPerPersonCsv}
                      title={`Export the ${allocateView === 'game' ? 'Per Game' : 'Per Person'} table as a CSV file`}
                      style={{ background: '#fff', border: '1px solid #1565c0', color: '#1565c0', padding: '0.3rem 0.75rem', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={allocateView === 'game' ? exportPerGamePdf : exportPerPersonPdf}
                      title={`Open a print-ready ${allocateView === 'game' ? 'Per Game' : 'Per Person'} view (use your browser's "Save as PDF" option)`}
                      style={{ background: '#fff', border: '1px solid #A4161A', color: '#A4161A', padding: '0.3rem 0.75rem', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}
                    >
                      Print / PDF
                    </button>
                  </div>
                </div>

                {/* PER PERSON TAB */}
                {allocateView === 'person' && (
                <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #1565c0', textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem' }}>Name</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center' }}>Referee</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center' }}>Bench</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center' }}>Total</th>
                        <th style={{ padding: '0.5rem', textAlign: 'right' }}>Pay</th>
                        <th style={{ padding: '0.5rem', textAlign: 'center' }}>Fairness</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocateResults.summary.map(person => {
                        const avg = allocateResults.summary.length > 0
                          ? allocateResults.summary.reduce((s, p) => s + p.totalGames, 0) / allocateResults.summary.length
                          : 0;
                        const deviation = person.totalGames - avg;
                        let flag = '';
                        let flagColor = '#2e7d32';
                        if (deviation >= 2) { flag = 'High'; flagColor = '#e65100'; }
                        else if (deviation <= -2) { flag = 'Low'; flagColor = '#c62828'; }
                        return (
                          <tr key={person.personId} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '0.4rem 0.5rem', fontWeight: 500 }}>{person.name}</td>
                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>{person.refereeGames || '-'}</td>
                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>{person.benchGames || '-'}</td>
                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', fontWeight: 700 }}>{person.totalGames}</td>
                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: '#1565c0', fontWeight: 600 }}>${person.totalPay.toFixed(2)}</td>
                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                              {flag ? <span style={{ background: flagColor + '20', color: flagColor, padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700 }}>{flag}</span> : <span style={{ color: '#2e7d32', fontSize: '0.8rem' }}>OK</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}

                {/* PER GAME TAB — proposed crew on every game (or a Skipped
                    badge when locked / has confirmed attendance). One
                    referee column per slot (CC / R / T) so the operator
                    sees who's filling each role. Empty cells flag unfilled
                    slots — the cannot-make table above explains why. */}
                {allocateView === 'game' && (
                <div style={{ overflowX: 'auto', marginBottom: '1.25rem', maxHeight: 420, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 6 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#eceff1' }}>
                      <tr style={{ textAlign: 'left' }}>
                        <th style={{ padding: '0.5rem' }}>Day</th>
                        <th style={{ padding: '0.5rem' }}>Date</th>
                        <th style={{ padding: '0.5rem' }}>Time</th>
                        <th style={{ padding: '0.5rem' }}>Court</th>
                        <th style={{ padding: '0.5rem' }}>Division</th>
                        <th style={{ padding: '0.5rem' }}>Matchup</th>
                        <th style={{ padding: '0.5rem' }}>Crew Chief</th>
                        <th style={{ padding: '0.5rem' }}>Referee 2</th>
                        <th style={{ padding: '0.5rem' }}>Referee 3</th>
                        <th style={{ padding: '0.5rem' }}>Referee 4</th>
                        <th style={{ padding: '0.5rem' }}>Bench</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...allocateResults.allocations]
                        .sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime))
                        .map((a) => {
                          const t = new Date(a.scheduledTime);
                          const dStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
                          const tStr = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
                          const refs = a.referees || [];
                          const bench = a.benchOfficials || [];
                          const slotCell = (val) => val
                            ? <span>{val.name || `#${val.personId}`}</span>
                            : <span style={{ color: '#bbb' }}>—</span>;
                          const benchCell = bench.length > 0
                            ? <span>{bench.map(b => b.name || `#${b.personId}`).join(', ')}</span>
                            : <span style={{ color: '#bbb' }}>—</span>;
                          return (
                            <tr key={a.gameId} style={{ borderBottom: '1px solid #eee', background: a.skipped ? '#fafafa' : undefined }}>
                              <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap', fontWeight: 600 }}>{dowShort(dStr)}</td>
                              <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{dStr}</td>
                              <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{tStr}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{a.court}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{a.division || '—'}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>
                                {a.team1} vs {a.team2}
                                {a.skipped && (
                                  <span style={{ marginLeft: 6, background: '#fff3e0', color: '#e65100', padding: '0.1rem 0.4rem', borderRadius: 3, fontSize: '0.7rem', fontWeight: 700 }}>
                                    Skipped: {a.reason}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{slotCell(refs[0])}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{slotCell(refs[1])}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{slotCell(refs[2])}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{slotCell(refs[3])}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{benchCell}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={handleApplyAllocation}
                    disabled={applyingAllocation}
                    style={{
                      flex: 1, background: '#2e7d32', color: 'white', border: 'none', padding: '0.75rem',
                      borderRadius: '8px', fontWeight: 600, fontSize: '0.95rem', cursor: applyingAllocation ? 'wait' : 'pointer'
                    }}
                  >
                    {applyingAllocation ? 'Applying...' : 'Apply Allocation'}
                  </button>
                  <button
                    onClick={() => { setAllocateResults(null); setAllocateView('person'); }}
                    disabled={applyingAllocation}
                    style={{
                      flex: 1, background: '#fff', color: '#666', border: '1px solid #ddd', padding: '0.75rem',
                      borderRadius: '8px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                    }}
                  >
                    Re-run
                  </button>
                  <button
                    onClick={() => { setShowAllocateModal(false); setAllocateResults(null); setAllocateView('person'); }}
                    disabled={applyingAllocation}
                    style={{
                      flex: 1, background: '#fff', color: '#c62828', border: '1px solid #ef9a9a', padding: '0.75rem',
                      borderRadius: '8px', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer'
                    }}
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ SUSPENDED PLAYERS (Judiciary) ============ */}
      {suspensionModalGame && (
        <div
          onClick={() => setSuspensionModalGame(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, padding: '1.25rem 1.5rem', maxWidth: 620, width: '95%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h2 style={{ margin: 0, color: '#c62828' }}>Suspended players</h2>
              <button onClick={() => setSuspensionModalGame(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>
            <p style={{ margin: '0 0 0.75rem', color: '#555', fontSize: '0.9rem' }}>
              {suspensionModalGame.team1} vs {suspensionModalGame.team2} — {suspensionModalGame.division}
            </p>
            <table className="admin-table" style={{ fontSize: '0.9rem' }}>
              <thead>
                <tr><th>Player</th><th>Team</th><th>Start</th><th>End</th></tr>
              </thead>
              <tbody>
                {(gameSuspensions[suspensionModalGame.id] || []).map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 700 }}>{s.playerName}</td>
                    <td>{s.team}</td>
                    <td>{new Date(`${s.startDate}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td>{new Date(`${s.endDate}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setSuspensionModalGame(null)} style={{ background: 'white', border: '1px solid #999', borderRadius: 4, padding: '0.45rem 1rem', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ GAME REPORT MODAL (court supervisor) ============ */}
      {reportModalGame && (
        <div
          className="modal-overlay"
          onClick={() => { if (!reportSaving) setReportModalGame(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 8, padding: '1.25rem 1.5rem', maxWidth: 860, width: '95%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
              <h2 style={{ margin: 0, color: '#37474f' }}>Game Report</h2>
              <button onClick={() => setReportModalGame(null)} disabled={reportSaving} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>
            <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.9rem' }}>
              {reportModalGame.team1} vs {reportModalGame.team2} — {reportModalGame.division} — {reportModalGame.court}
            </p>

            {/* Forfeit */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Forfeit</label>
              <select
                value={reportForfeit}
                onChange={e => setReportForfeit(e.target.value)}
                disabled={readOnly}
                style={{ padding: '0.4rem', border: '1px solid #ccc', borderRadius: 4, minWidth: 260 }}
              >
                <option value="">None</option>
                <option value={reportModalGame.team1}>{reportModalGame.team1}</option>
                <option value={reportModalGame.team2}>{reportModalGame.team2}</option>
              </select>
            </div>

            {/* Tech Fouls */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Tech Fouls</label>
              {reportTechFouls.length === 0 && (
                <p style={{ margin: '0 0 6px', color: '#888', fontSize: '0.85rem' }}>None recorded.</p>
              )}
              {reportTechFouls.map((t, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  {renderPlayerPicker(t, next => setReportTechFouls(prev => prev.map((row, i) => i === idx ? next : row)))}
                  <select
                    value={t.count} disabled={readOnly}
                    onChange={e => setReportTechFouls(prev => prev.map((row, i) => i === idx ? { ...row, count: parseInt(e.target.value) } : row))}
                    style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4 }}
                  >
                    <option value={1}>1 tech foul</option>
                    <option value={2}>2 tech fouls</option>
                  </select>
                  <select
                    value={t.severity || 'Low'} disabled={readOnly}
                    onChange={e => setReportTechFouls(prev => prev.map((row, i) => i === idx ? { ...row, severity: e.target.value } : row))}
                    style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4, color: (t.severity || 'Low') === 'High' ? '#c62828' : undefined, fontWeight: (t.severity || 'Low') === 'High' ? 700 : 400 }}
                    title="Severity"
                  >
                    <option value="Low">Low severity</option>
                    <option value="High">High severity</option>
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
                    <input
                      type="checkbox" checked={!!t.ejection} disabled={readOnly}
                      onChange={e => setReportTechFouls(prev => prev.map((row, i) => i === idx ? { ...row, ejection: e.target.checked } : row))}
                    />
                    Ejection
                  </label>
                  <input
                    type="text" placeholder="Comments" value={t.comments} disabled={readOnly}
                    onChange={e => setReportTechFouls(prev => prev.map((row, i) => i === idx ? { ...row, comments: e.target.value } : row))}
                    style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4, flex: 1, minWidth: 160 }}
                  />
                  {!readOnly && (
                    <button
                      onClick={() => setReportTechFouls(prev => prev.filter((_, i) => i !== idx))}
                      title="Remove this row"
                      style={{ background: 'none', border: 'none', color: '#c62828', fontSize: '1.1rem', cursor: 'pointer', fontWeight: 700 }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (
                <button
                  onClick={() => setReportTechFouls(prev => [...prev, { participantId: null, team: '', name: '', count: 1, severity: 'Low', ejection: false, comments: '' }])}
                  style={{ background: 'white', border: '1px solid #1565c0', color: '#1565c0', borderRadius: 4, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
                >
                  + Add tech foul
                </button>
              )}
            </div>

            {/* Injuries */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Injuries</label>
              {reportInjuries.length === 0 && (
                <p style={{ margin: '0 0 6px', color: '#888', fontSize: '0.85rem' }}>None recorded.</p>
              )}
              {reportInjuries.map((inj, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  {renderPlayerPicker(inj, next => setReportInjuries(prev => prev.map((row, i) => i === idx ? next : row)))}
                  <input
                    type="text" placeholder="What was injured" value={inj.injury} disabled={readOnly}
                    onChange={e => setReportInjuries(prev => prev.map((row, i) => i === idx ? { ...row, injury: e.target.value } : row))}
                    style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4, width: 170 }}
                  />
                  <select
                    value={inj.treatment} disabled={readOnly}
                    onChange={e => setReportInjuries(prev => prev.map((row, i) => i === idx ? { ...row, treatment: e.target.value } : row))}
                    style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4 }}
                  >
                    <option value="">Treatment…</option>
                    <option value="Ice">Ice</option>
                    <option value="First Aid">First Aid</option>
                    <option value="Medical Centre">Medical Centre</option>
                    <option value="Urgent Care">Urgent Care</option>
                    <option value="Ambulance">Ambulance</option>
                  </select>
                  <input
                    type="text" placeholder="How the injury occurred" value={inj.comments} disabled={readOnly}
                    onChange={e => setReportInjuries(prev => prev.map((row, i) => i === idx ? { ...row, comments: e.target.value } : row))}
                    style={{ padding: '0.35rem', border: '1px solid #ccc', borderRadius: 4, flex: 1, minWidth: 160 }}
                  />
                  {!readOnly && (
                    <button
                      onClick={() => setReportInjuries(prev => prev.filter((_, i) => i !== idx))}
                      title="Remove this row"
                      style={{ background: 'none', border: 'none', color: '#c62828', fontSize: '1.1rem', cursor: 'pointer', fontWeight: 700 }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (
                <button
                  onClick={() => setReportInjuries(prev => [...prev, { participantId: null, team: '', name: '', injury: '', treatment: '', comments: '' }])}
                  style={{ background: 'white', border: '1px solid #1565c0', color: '#1565c0', borderRadius: 4, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
                >
                  + Add injury
                </button>
              )}
            </div>

            {/* General Info */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>General Info</label>
              <textarea
                value={reportNotes} disabled={readOnly}
                onChange={e => setReportNotes(e.target.value)}
                placeholder="Anything else worth recording about this game…"
                rows={5}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: 4, fontFamily: 'inherit', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>

            {reportError && (
              <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '0.75rem' }}>
                {reportError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setReportModalGame(null)} disabled={reportSaving}
                style={{ background: 'white', border: '1px solid #999', borderRadius: 4, padding: '0.45rem 1rem', cursor: 'pointer' }}
              >
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {!readOnly && (
                <button
                  onClick={saveGameReport} disabled={reportSaving}
                  style={{ background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, padding: '0.45rem 1.25rem', cursor: 'pointer', fontWeight: 700 }}
                >
                  {reportSaving ? 'Saving…' : 'Save report'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ DAILY NOTES MODAL ============ */}
      {showDayNotesModal && (
        <div
          className="modal-overlay"
          onClick={() => { if (!dayNotesSaving) setShowDayNotesModal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 8, padding: '1.25rem 1.5rem', maxWidth: 620, width: '95%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
              <h2 style={{ margin: 0, color: '#37474f' }}>Daily Notes</h2>
              <button onClick={() => setShowDayNotesModal(false)} disabled={dayNotesSaving} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>
            <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.9rem' }}>
              General Info for <strong>{selectedDate}</strong> — a comment about the day itself, not a
              specific match. It appears in the Court Supervisors report's General Info section.
            </p>
            <textarea
              value={dayNotesDraft}
              onChange={e => setDayNotesDraft(e.target.value)}
              placeholder="Anything worth recording about the day…"
              rows={7}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: 4, fontFamily: 'inherit', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '0.75rem' }}
            />
            {dayNotesError && (
              <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '0.75rem' }}>
                {dayNotesError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setShowDayNotesModal(false)} disabled={dayNotesSaving}
                style={{ background: 'white', border: '1px solid #999', borderRadius: 4, padding: '0.45rem 1rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={saveDayNotes} disabled={dayNotesSaving}
                style={{ background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, padding: '0.45rem 1.25rem', cursor: 'pointer', fontWeight: 700 }}
              >
                {dayNotesSaving ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ ROSTER IMPORT MODAL ============ */}
      {showImportModal && (
        <div
          className="modal-overlay"
          onClick={() => { if (!importLoading && !applyingImport) setShowImportModal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 8, padding: '1.25rem 1.5rem', maxWidth: 1100, width: '95%', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, color: '#37474f' }}>Import Roster</h2>
              <button onClick={() => setShowImportModal(false)} disabled={importLoading || applyingImport} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>&times;</button>
            </div>

            <p style={{ color: '#555', fontSize: '0.9rem', marginTop: 0 }}>
              Upload an image (PNG/JPG) or PDF of an externally-prepared roster. The system will OCR
              the table, match each row to a game on the chosen date by time + court, and resolve
              names to people on file. You'll review the preview before anything is written. If a
              person appears in two cells at the same time slot they're assigned only to the first
              game in that slot.
            </p>

            {!importPreview && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'end', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Roster file (image or PDF)</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportError(''); }}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Roster date</label>
                  <input
                    type="date"
                    value={importDate}
                    onChange={(e) => { setImportDate(e.target.value); setImportError(''); }}
                    style={{ width: '100%', padding: '0.4rem', border: '1px solid #999', borderRadius: 4 }}
                  />
                </div>
              </div>
            )}

            {importError && (
              <div style={{ padding: '0.6rem 0.8rem', background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 4, color: '#c62828', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                {importError}
              </div>
            )}

            {!importPreview && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button onClick={() => setShowImportModal(false)} disabled={importLoading} style={{ padding: '0.5rem 1rem', background: '#f5f5f5', border: '1px solid #999', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleImportPreview} disabled={importLoading || !importFile || !importDate} style={{ padding: '0.5rem 1.25rem', background: '#37474f', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                  {importLoading ? 'Scanning…' : 'Scan + Preview'}
                </button>
              </div>
            )}

            {importPreview && (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, background: '#e8f5e9', borderRadius: 6, padding: '0.6rem', textAlign: 'center', minWidth: 120 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2e7d32' }}>{importPreview.allocations.length}</div>
                    <div style={{ fontSize: '0.75rem', color: '#2e7d32' }}>Games matched</div>
                  </div>
                  {importPreview.unmatchedGames.length > 0 && (
                    <div style={{ flex: 1, background: '#fff3e0', borderRadius: 6, padding: '0.6rem', textAlign: 'center', minWidth: 120 }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#e65100' }}>{importPreview.unmatchedGames.length}</div>
                      <div style={{ fontSize: '0.75rem', color: '#e65100' }}>Rows with no matching game</div>
                    </div>
                  )}
                  {importPreview.unmatchedNames.length > 0 && (
                    <div style={{ flex: 1, background: '#ffebee', borderRadius: 6, padding: '0.6rem', textAlign: 'center', minWidth: 120 }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#c62828' }}>{importPreview.unmatchedNames.length}</div>
                      <div style={{ fontSize: '0.75rem', color: '#c62828' }}>Names with no person match</div>
                    </div>
                  )}
                  {importPreview.dedupNotes.length > 0 && (
                    <div style={{ flex: 1, background: '#e3f2fd', borderRadius: 6, padding: '0.6rem', textAlign: 'center', minWidth: 120 }}>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1565c0' }}>{importPreview.dedupNotes.length}</div>
                      <div style={{ fontSize: '0.75rem', color: '#1565c0' }}>Dedup'd duplicate slots</div>
                    </div>
                  )}
                </div>

                <h3 style={{ fontSize: '0.95rem', color: '#333', marginBottom: 4 }}>Preview ({importPreview.allocations.length} games)</h3>
                <div style={{ overflowX: 'auto', maxHeight: 320, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 6, marginBottom: '0.75rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5' }}>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>Time</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>Crt</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>CC</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>R</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>T</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>Bench</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.allocations.map((a, i) => {
                        const findRef = (slot) => a.referees.find(r => r.slot === slot);
                        return (
                          <tr key={a.gameId || i} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{a.time}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{a.court}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{findRef('CC')?.personId ? `#${findRef('CC').personId}` : <span style={{ color: '#bbb' }}>—</span>}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{findRef('R')?.personId ? `#${findRef('R').personId}` : <span style={{ color: '#bbb' }}>—</span>}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{findRef('T')?.personId ? `#${findRef('T').personId}` : <span style={{ color: '#bbb' }}>—</span>}</td>
                            <td style={{ padding: '0.35rem 0.4rem' }}>{a.benchOfficials[0]?.personId ? `#${a.benchOfficials[0].personId}` : <span style={{ color: '#bbb' }}>—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {importPreview.unmatchedNames.length > 0 && (
                  <>
                    <h3 style={{ fontSize: '0.95rem', color: '#c62828', marginBottom: 4 }}>Names that didn't match any person ({importPreview.unmatchedNames.length})</h3>
                    <div style={{ marginBottom: '0.75rem', padding: '0.5rem', background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 4, fontSize: '0.82rem', maxHeight: 140, overflowY: 'auto' }}>
                      {importPreview.unmatchedNames.map((u, i) => (
                        <div key={i} style={{ marginBottom: 2 }}>
                          <strong>{u.name}</strong> — {u.slot} on {u.time} Crt {u.court}
                        </div>
                      ))}
                      <div style={{ marginTop: 4, color: '#666' }}>Add these people in People → Referees / Bench Officials and re-run the import.</div>
                    </div>
                  </>
                )}

                {importPreview.unmatchedGames.length > 0 && (
                  <>
                    <h3 style={{ fontSize: '0.95rem', color: '#e65100', marginBottom: 4 }}>Rows with no matching game ({importPreview.unmatchedGames.length})</h3>
                    <div style={{ marginBottom: '0.75rem', padding: '0.5rem', background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 4, fontSize: '0.82rem', maxHeight: 140, overflowY: 'auto' }}>
                      {importPreview.unmatchedGames.map((u, i) => (
                        <div key={i} style={{ marginBottom: 2 }}>
                          {u.time} Crt {u.court} {u.teamA && u.teamB ? `— ${u.teamA} vs ${u.teamB}` : ''}
                        </div>
                      ))}
                      <div style={{ marginTop: 4, color: '#666' }}>No Games row exists for this date / time / court. Either fix the game schedule first or accept that these rows are ignored.</div>
                    </div>
                  </>
                )}

                {importPreview.dedupNotes.length > 0 && (
                  <>
                    <h3 style={{ fontSize: '0.95rem', color: '#1565c0', marginBottom: 4 }}>Dedup notes — assigned to first game in slot only ({importPreview.dedupNotes.length})</h3>
                    <div style={{ marginBottom: '0.75rem', padding: '0.5rem', background: '#e3f2fd', border: '1px solid #64b5f6', borderRadius: 4, fontSize: '0.82rem', maxHeight: 120, overflowY: 'auto' }}>
                      {importPreview.dedupNotes.map((d, i) => (
                        <div key={i}>{d.name} — {d.slot} at {d.time} Crt {d.court}: {d.note}</div>
                      ))}
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <button onClick={() => { setImportPreview(null); setImportFile(null); }} disabled={applyingImport} style={{ padding: '0.5rem 1rem', background: '#f5f5f5', border: '1px solid #999', borderRadius: 4, cursor: 'pointer' }}>Re-upload</button>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => setShowImportModal(false)} disabled={applyingImport} style={{ padding: '0.5rem 1rem', background: '#f5f5f5', border: '1px solid #999', borderRadius: 4, cursor: 'pointer' }}>Discard</button>
                    <button onClick={handleImportApply} disabled={applyingImport || importPreview.allocations.length === 0} style={{ padding: '0.5rem 1.25rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                      {applyingImport ? 'Applying…' : `Apply ${importPreview.allocations.length} game(s)`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
